import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { cloneDeep } from '@apollo/client/utilities'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import {
    AddUpdateBestPracticeResponse,
    BestPracticeData,
    RemoveBestPracticeResponse,
} from '@deltek/specification-client/core/Models/bestPractice'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateViewItem } from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    ChildTextContentElement,
    OptionItemState,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { BestPracticesSetting } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
import {
    compareBestPracticesMutation,
    getIsBP,
} from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { CircularProgress } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { SetProjectContentItemElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { GetFamilyElementChanges } from 'api/graphQL/queries/changes.queries'
import { WorkspaceProjectElementNode, setElementTrackedChanges } from 'api/graphQL/reactiveVars/element'
import {
    resetOnFocusProperty,
    setPropertyToFocus,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    setIsTemplateDataLastItem,
    setPromoteDemoteWorkspaceTemplateData,
    setReactiveWorkspaceActions,
    setWorkspaceTemplateData,
    templateData,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    UpdateProjectContentsforPill,
    isContentTeam,
    isOmWorkspaceUser,
    reactiveWorkspaceContents,
    setElementIdOnEdit,
    setWorkspaceContentItems,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { TrackChangeStatusMap, TrackChangeType } from 'models/changes'
import { UnitsOfMeasure } from 'models/projectSettings'
import { TextContentElementInput } from 'models/setProjectContentItemElementsInput'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import sanitizeHtml from 'sanitize-html'
import { useIsBestPracticeTagDisabled } from 'shared/utils/bestPracticeUtils'
import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WorkspaceManufacturerSelected,
    getFromLocalStorage,
    isPropertyKeyEditable,
} from 'shared/utils/workspaceUtils'
import { v4 as newGuid } from 'uuid'
import PropertyEditorEditableText, {
    sanitizeAndDecodeValue,
} from '../PropertyEditor/PropertyEditorEditableText'
import PropertyEditorOptionItem from '../PropertyEditor/PropertyEditorOptionItem'
import { getUpdatedItemsArray } from '../WorkspaceContentArea'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import {
    TextVariant,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextVariants,
} from '../constants'
import { AreaContext } from './AreaContext'
import { BestPracticeDataResponseExtended } from './BestPracticeSyncTypes'
import { DragDropAreaContext } from './DragDropAreaContext'
import { TabsContext } from './TabsContext'
import {
    KeyErrorMessage,
    ProductGroupKeyErrorMessage,
    getPropertyEditingDetails,
} from './addUtils'
import { useInsertUomPill } from './hooks'
import {
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from './model'
import {
    UpdateableTypes,
    WorkspaceTabTitles,
    addNewElementAtLastInTemplate,
    findBODElementFromContent,
    getIsDeletingWholeParagraph,
    getUpdatedPTElementsWithAppendedColon,
    getUpdatedTemplate,
    getUpdatedTemplateAfterPillAdd,
    getUpdatedTemplateAfterPillRemove,
    getUpdatedTemplateAfterUOMGrouping,
    getUpdatedUOMInPillTemplate,
    makeElementsApiReady,
} from './utils'

const forbiddenParagraphCharacters = ['|']
const forbiddenParagraphCharactersForKeyedParagraph = ['|', ':']

export interface ExtendedTextContentElement extends TextContentElement {}
export interface ContentItemElementTextStyleProps {
    element: Partial<TextContentElement>
    template: NormalizedDocumentTemplateViewItem
    isHidden?: boolean
    parentTemplate?: NormalizedDocumentTemplateViewItem
}
export interface BODProductItem {
    bodText: string
    id: string
}

export const DRAGGABLE_ITEM_BORDER_RADIUS = 4
export interface ContentItemElementTextProps
    extends ContentItemElementTextStyleProps {
    showMetric?: boolean
    showImperial?: boolean
    saveNewlyAddedProperty: (
        newValue: string,

        indexOfPillForNewPt?: number | undefined,
        typeOfPillForNewPt?: string | undefined,
        isPillWithNewPara?: boolean
    ) => void
    editable?: boolean
    onElementUpdate?: (template: NormalizedDocumentTemplateViewItem) => void
    elementIndex?: number
    isLastProperty: boolean
    cancelAddProperty: (isFromDialog: boolean) => void
    isCancelCancelled?: boolean
    getSiblings: (myContentItemId: string) => string[]
    getParentDetails: () => {
        contentItemParentId: string
        parentKey: string
        parentType: string
    }
    setEditError?: (value: boolean) => void
    isEditError?: boolean
    prefix?: string
    isParentProductGroup?: boolean
    selectedItemIdForPills?: string
    isDraftMode?: boolean
    contentTeamFromStudioPage?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    selectedContent?: string
    setPromoteDemoteRequirements?: (input?: PromoteDemoteRequirements) => void
    triggerAddNewProperty: () => void
    onHtmlParagraphPaste?: (pastedHtml: string) => void
    areHiddenPropertiesVisible?: boolean
    setProperty?: (input?: any) => void
    productTypeNodeInFamily?: Partial<ProjectElementNode>
    deleteFamilyContent?: () => void
    checkIfBestPracticeIsOutOfSync?: (state: any) => void
    onBestPracticesMutationData?: (
        mutationData: Partial<BestPracticeData> | null,
        mutationInput?: any
    ) => void
}

export const getParentContentId = (
    template: NormalizedDocumentTemplateViewItem,
    fieldState: Partial<TextContentElement>,
    childUOMInPill?: boolean
) => {
    const fieldId = fieldState?.id
    const parentTemplate = cloneDeep(template)
    let parentId: string | null = null
    if (parentTemplate) {
        parentTemplate?.contentItem?.elements?.forEach(
            (parentContentItemElement: TextContentElementInput) => {
                let isParentIdFound = false
                if (
                    parentContentItemElement.type === 'OptionGroup' ||
                    childUOMInPill
                ) {
                    if (
                        childUOMInPill &&
                        parentContentItemElement?.type === 'newPill'
                    ) {
                        isParentIdFound = true
                    } else {
                        parentContentItemElement.elements?.forEach(
                            (
                                childContentItemElement: TextContentElementInput
                            ) => {
                                if (childContentItemElement.id === fieldId) {
                                    isParentIdFound = true
                                }
                            }
                        )
                    }

                    if (isParentIdFound) {
                        if (parentContentItemElement.id)
                            parentId = parentContentItemElement.id
                    }
                }
            }
        )
    }

    return parentId
}

/**
 * Formats selection option text based on unit of measure preferences.
 *
 * Performance optimization: If the text field is already populated, it means
 * the formatting has already been applied (e.g., by useFormatReplacementOption hook),
 * so we can skip the expensive element processing logic below.
 *
 * @param selOptionElement - The selection option element to format
 * @param type - The unit of measure type (imperial, metric, or both)
 * @returns Formatted text string
 */
export const formatSelectionOption = (
    selOptionElement: Partial<TextContentElement>,
    type: string
) => {
    // Early return if text is already formatted (performance optimization)
    // Check for truthy value to handle undefined, null, and empty string
    if (selOptionElement.text) {
        return selOptionElement.text
    }
    if (UnitsOfMeasure.BOTH.toLowerCase().includes(type)) {
        const bothTextElements =
            selOptionElement?.elements &&
            selOptionElement?.elements.map((item) => {
                let sampleArray: string[] = []
                let elementText: string = ''
                if (item?.imperialUnits === '') {
                    elementText = item?.metricUnits ?? ''
                } else if (item?.metricUnits === '') {
                    elementText = item?.imperialUnits ?? ''
                } else {
                    elementText = `${item.imperialUnits} (${item.metricUnits})`
                }
                if (!!item?.text) {
                    elementText = `${elementText}${item.text}`
                }
                sampleArray?.push(elementText ?? '')
                return sampleArray
            })
        return bothTextElements?.join('')
    } else if (UnitsOfMeasure.IMPERIAL.toLowerCase().includes(type)) {
        const imperialTextElement =
            selOptionElement?.elements &&
            selOptionElement?.elements.map((item) => {
                let sampleArray: string[] = []
                let elementText: string = item.imperialUnits ?? ''
                if (!!item?.text) {
                    elementText = `${elementText}${item.text}`
                }
                sampleArray?.push(elementText)
                return sampleArray
            })
        return imperialTextElement?.join('')
    } else if (UnitsOfMeasure.METRIC.toLowerCase().includes(type)) {
        const metricTextElement =
            selOptionElement?.elements &&
            selOptionElement?.elements.map((item) => {
                let sampleArray: string[] = []
                let elementText: string = item.metricUnits ?? ''
                if (!!item?.text) {
                    elementText = `${elementText}${item.text}`
                }
                sampleArray?.push(elementText)
                return sampleArray
            })
        return metricTextElement?.join('')
    }
    return ''
}

const useStyles = makeStyles((theme: Theme) => ({
    container: (props: ContentItemElementTextStyleProps) => ({
        fontSize: pxToRem(14),
        color:
            props.element.metricUnits || props.element.imperialUnits
                ? props?.isHidden
                    ? '#F7890940'
                    : '#F78909'
                : props?.isHidden
                ? '#5E5F6140'
                : '#5E5F61',
        fontWeight: props.element.isOptionItem ? 'bold' : 400,
        lineHeight: '30px',
        whiteSpace: 'pre-wrap',
    }),
    editorComponentContainer: {
        '& > button': {
            margin: theme.spacing(0.25, 0),
        },
        width: 'inherit',
        position: 'relative',
    },
    editorComponentContainerFlex: {
        display: 'flex',
    },
    dynamicText: {
        fontWeight: 500,
        '& > *': {
            margin: theme.spacing(0.25, 0),
        },
    },
    addedPropertyKey: {
        fontSize: pxToRem(14),
        color: '#A78966',
        fontStyle: 'italic',
        fontWeight: 400,
    },
    borderRight: {
        borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    borderBottom: {
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    contentItemContainer: {
        display: 'flex',
        boxSizing: 'inherit',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        margin: theme.spacing(0.125),
    },
    draggedItemErrorDarkBorder: {
        border: '1px solid #EB5757',
    },
    draggedItemErrorLightBackgroundColor: {
        backgroundColor: '#FFDEDE',
        borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    newPropertyLoading: {
        color: theme.palette.common.white,
    },
    contentItemDragHandleContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        borderTopLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    draggedItemErrorDarkBackgroundColor: {
        backgroundColor: '#EB5757',
        marginRight: 10,
    },
    highlightElement: (props: ContentItemElementTextStyleProps) => ({
        border: '1px solid rgba(0, 152, 214, 0.5)',
        boxShadow: '0px 1px 4px rgba(0, 152, 214, 0.65)',
        borderRadius: pxToRem(100),
        padding: theme.spacing(
            0.75,
            props?.element?.type === TextVariants.UnitsOfMeasure ? 0 : 1
        ),
        width: 'auto',
    }),
}))

const removeUnitOfMeasureDelimiters = (text: string | undefined) => {
    return text
        ? text.replace(/\|START_IP\||\|END_IP\||\|START_SI\||\|END_SI\|/g, '')
        : ''
}

export const getDisplayText = (
    element: Partial<TextContentElement>,
    showMetric?: boolean,
    showImperial?: boolean,
    unitsOfMeasure?: UnitsOfMeasure,
    isRevert?: boolean
) => {
    let text = element.text !== '' ? element.text : ''
    if (element.type === 'UnitsOfMeasure' || element.type === 'newUOM') {
        text = showImperial ? element.imperialUnits : element.metricUnits
    }
    if (
        (element.type === 'SelectionOption' ||
            (element.type === 'ReplacementOption' &&
                element.elements?.length! > 0 &&
                (element.optionItemState !== 'OptedIn' || isRevert))) &&
        (showMetric || showImperial)
    ) {
        text = formatSelectionOption(
            element,
            unitsOfMeasure
                ? unitsOfMeasure.toLowerCase()
                : UnitsOfMeasure.IMPERIAL.toLowerCase()
        )
    }
    if (element.type === 'ReplacementOption') {
        // Work around data issue where replacement options may contain delimiters left over from the importer
        text = removeUnitOfMeasureDelimiters(text)
    }

    return sanitizeAndDecodeValue(text!)
}
export const formatSpace = (stringValue?: string) => {
    return stringValue?.replace('&nbsp;', ' ')
}
type UpdatedContentElement = Partial<
    Pick<
        ChildTextContentElement,
        | 'elements'
        | 'id'
        | 'imperialUnits'
        | 'metricUnits'
        | 'text'
        | 'type'
        | 'placeholderText'
        | 'changeType'
    >
>

// Displays the text based on the TextContentElement
export const getUpdatedElements = ({
    fieldStateId,
    fieldStateText,
    positionIndex,
    type,
    fieldStateChangeType,
    fieldState,
}: Partial<{
    fieldStateId: string
    fieldStateText: string
    positionIndex: number
    type: TextVariant
    fieldStateChangeType: ChildTextContentElement['changeType']
    fieldState: Partial<TextContentElement>
}>): UpdatedContentElement[] => {
    const defaultElementProps: UpdatedContentElement = {
        elements: [],
        imperialUnits: '',
        metricUnits: '',
        text: '',
        type: TextVariants.Text,
        placeholderText: undefined,
        changeType: 'ADDED',
    }

    switch (type) {
        case TextVariants.newPill:
        case TextVariants.newReplacement:
        case TextVariants.newUOM: {
            return [
                {
                    ...defaultElementProps,
                    id: fieldStateId,
                    text: fieldStateText?.substring(0, positionIndex),
                    changeType: fieldStateChangeType,
                },
                {
                    ...defaultElementProps,
                    id: newGuid(),
                    type,
                    placeholderText: '',
                },
                {
                    ...defaultElementProps,
                    id: newGuid(),
                    text:
                        fieldStateText?.substring(
                            positionIndex!!,
                            fieldStateText?.length
                        ) !== ''
                            ? fieldStateText?.substring(
                                  positionIndex!!,
                                  fieldStateText?.length
                              )
                            : ' ',
                    placeholderText: '',
                    changeType:
                        fieldStateText!!?.substring(
                            positionIndex!!,
                            fieldStateText?.length
                        )?.length > 0
                            ? fieldStateChangeType
                            : null,
                },
            ]
        }
        case TextVariants.uomInPill:
            const uomInPillElements = [
                {
                    ...defaultElementProps,
                    id: fieldStateId,
                    changeType: fieldStateChangeType,
                },
                {
                    ...defaultElementProps,
                    id: newGuid(),
                    type,
                    placeholderText: '',
                },
                {
                    ...defaultElementProps,
                    id: newGuid(),
                    placeholderText: '',
                    changeType: fieldStateChangeType,
                },
            ]

            const isAnyElements =
                !!fieldState?.elements && fieldState?.elements?.length > 1
            // have pre/trailing text? (type='Text') append them to 1st and last uom pill elements accordingly.
            if (
                !!fieldStateText &&
                fieldStateText !== ' ' &&
                fieldStateText.length >= positionIndex! &&
                !isAnyElements
            ) {
                uomInPillElements[0].text = fieldStateText.substring(
                    0,
                    positionIndex
                )
                uomInPillElements[2].text = fieldStateText.substring(
                    positionIndex!!,
                    fieldStateText?.length
                )
            }

            // remove the pre-text element to reduce spacing in between UOMs
            if (isAnyElements) {
                uomInPillElements.splice(0, 1)
            }
            return uomInPillElements
        default:
            return []
    }
}
export const ContentItemElementText: React.FC<ContentItemElementTextProps> = ({
    element,
    template,
    showMetric = false,
    saveNewlyAddedProperty,
    showImperial = false,
    editable = false,
    onElementUpdate,
    elementIndex,
    isHidden,
    cancelAddProperty,
    isCancelCancelled,
    getSiblings,
    getParentDetails,
    setEditError,
    isEditError,
    prefix,
    isParentProductGroup,
    selectedItemIdForPills,
    isDraftMode,
    contentTeamFromStudioPage,
    selectedTopic,
    selectedContent,
    setPromoteDemoteRequirements,
    triggerAddNewProperty,
    onHtmlParagraphPaste,
    areHiddenPropertiesVisible,
    parentTemplate,
    setProperty,
    productTypeNodeInFamily,
    deleteFamilyContent,
    checkIfBestPracticeIsOutOfSync,
    onBestPracticesMutationData,
}) => {
    const classes = useStyles({ element, template, isHidden })
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const { projectId } = useParams<{ projectId?: string }>()
    const [hasChanges, setHasChanges] = React.useState(false)
    const [hasError, setHasError] = React.useState(false)
    const [isPillAdded, setIsPillAdded] = React.useState<boolean>(false)
    const [isBPMenuOpen, setIsBPMenuOpen] = React.useState<boolean>(false)
    const [mouseOverElement, setMouseOverElement] = React.useState<
        string | null
    >(null)
    const [bodItem, setBodItem] = React.useState<BODProductItem>({
        bodText: '',
        id: '',
    })

    const { projectData } = React.useContext(ProjectPageContext)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const { typeForFullView } = React.useContext(DragDropAreaContext)
    const [popoverAnchor, setPopoverAnchor] = React.useState<null | Element>(
        null
    )
    const [selectedPillElementId, setSelectedPillElementId] = React.useState<
        null | string
    >(null)

    const [selectedPillIsBP, setSelectedPillIsBP] = React.useState<boolean>(
        false
    )

    const [selectedPillIsGBP, setSelectedPillIsGBP] = React.useState<boolean>(
        false
    )

    const [editorRef, setEditorRef] = React.useState<
        React.RefObject<HTMLElement> | HTMLElement
    >()
    const [showPillDeleteDialog, setShowPillDeleteDialog] = React.useState(
        false
    )

    const isPillDeleteDialogVisible = React.useRef<boolean>(false)

    const elementRef = React.useRef<any | undefined | null>()

    const {
        propertyToFocus,
        propertyContentItemId,
        optionGroupToFocus,
    } = useReactiveVar(workspaceProperty)

    const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
        workspaceToggles
    )

    const {
        isAdmin,
        currentUserInfo: { kpUserId },
    } = useReactiveVar(userInfo)

    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)
    const { workspaceTemplateData } = useReactiveVar(templateData)

    const serviceWorker = navigator.serviceWorker
    const hasPreviousChanges = usePrevious(hasChanges)

    const [isFocusManaged, setIsFocusManaged] = React.useState<boolean>(false)
    const isDeleteFormatted = React.useMemo(
        () =>
            (!element?.authoredUserId ||
                kpUserId !== element?.authoredUserId!) ??
            false,
        [template, kpUserId, element]
    )

    const getPersistedState = React.useCallback(
        (propKey: string) => {
            return getFromLocalStorage(kpUserId, projectId!, propKey)
        },
        [kpUserId]
    )

    const {
        unitsOfMeasure,
        selectedProjectNode,
        manufacturerArticleIds,
        handleEditKeyChanges,
        handleChangeParagraphItemId,
        selectedParagraphActionState,
        handleChangeParagraphActionState,
        selectedParagraphItemId,
        retrieveUpdatedElementChanges,
        nugget,
        type,
        handleBestPracticeProperty,
    } = React.useContext(AreaContext)

    const isBPTagDisabledInput = {
        userInfo,
        isViewOnly: false,
        isInProject: selectedProjectNode?.isInProject,
        contentTeamFromStudioPage,
        isUserAddedPR: template?.isUserAdded,
        isProjectImported: reactiveWorkspaceContents()?.isProjectImported,
        isProjectOnly: selectedProjectNode?.isProjectOnly,
    }

    // checking if BP tags are disabled for editing — OM users cannot edit BP tags currently , only view them
    const isBestPracticeEditDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
    })

    const isOMWorkspace = React.useMemo(
        () =>
            isOMUser &&
            projectId === currentUserActiveFirm?.bestPracticeProjectId,
        [projectId, isOMUser, currentUserActiveFirm]
    )

    const isOMBestPracticeEnabled =
        processEnv.REACT_APP_FeatureFlag_IsOMBestPracticeEnabled === '1'

    const isOMBestPracticeContext = isOMBestPracticeEnabled && isOMWorkspace

    // checking if BP tags are disabled for viewing
    const isBestPracticeViewDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        isViewOnly: true,
        isOfficeMaster: isOMBestPracticeContext,
    })

    const isSustainabilityTopic = React.useMemo(() => {
        return type === 'Sustainability Topic'
    }, [type])
    const { handleRetrieveUpdatedDTV, contentItemWorker } = React.useContext(
        WorkspaceContentContext
    )

    const displayText = getDisplayText(
        element,
        showMetric,
        showImperial,
        unitsOfMeasure
    )

    const [fieldState, setFieldState] = React.useState<
        Partial<TextContentElement>
    >({
        ...element,
        text: displayText ?? '',
    })
    const { handleUpdateTemplate } = React.useContext(TabsContext)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const isNewAddedOptionGroup = useCallback(
        (data: Partial<UpdateProjectContentsforPill>) => {
            const {
                contentItemNodes,
                tabSelected,
                elementIdOnEdit,
                dtv,
            } = workspaceContents()
            const updatedContentItems = getUpdatedItemsArray(
                contentItemNodes ?? [],
                data?.setProjectContentItemElements?.contentItemNodes ??
                    ([] as Partial<ContentItem>[]),
                true,
                false
            )
            const dataUpdateTask = setTimeout(() => {
                contentItemWorker.postMessage({
                    dtv: dtv?.items ?? [],
                    selectedTab: WorkspaceTabTitles[tabSelected] as string,
                    workspaceContentItems: updatedContentItems ?? [],
                })
                contentItemWorker.onmessage = (
                    event: MessageEvent<ContentItem[]>
                ) => {
                    setWorkspaceContentItems(
                        updatedContentItems.concat(event.data)
                    )

                    setHasChanges(false)
                    if (template.id === elementIdOnEdit) {
                        setTimeout(() => {
                            setElementIdOnEdit(undefined)
                        }, 3000)
                    }
                }
                setIsPillAdded(false)
                clearTimeout(dataUpdateTask)
            }, 0)
        },
        [
            isPillAdded,
            getUpdatedItemsArray,
            setWorkspaceContentItems,
            hasChanges,
        ]
    )
    const checkPillWithText = usePrevious(fieldState)

    const setUomGrouping = useCallback(() => {
        let checkUOM: any = template?.contentItem?.elements

        let index = template?.contentItem?.elements?.findIndex(
            (x) => x.type === 'newUOM'
        )
        if (index && index > 0) {
            checkUOM!![index!!].type = 'UnitsOfMeasure'

            // contentItemNodeElements will have only required elemets
            let contentItemNodeElements = checkUOM.map(
                (data: {
                    originalText: string
                    isOptionItem: boolean
                    fieldId: string
                    isBestPractice: boolean
                    isGlobalBestPractice: boolean
                    isPremiumPlus: boolean
                    parentOptionGroupId: string
                    __typename: any
                }) => {
                    const {
                        isOptionItem,
                        fieldId,
                        isBestPractice,
                        isGlobalBestPractice,
                        isPremiumPlus,
                        parentOptionGroupId,
                        originalText,

                        __typename,
                        ...apiPilInput
                    } = data
                    return apiPilInput
                }
            )

            const updatedElementList = getUpdatedTemplateAfterUOMGrouping(
                template,
                contentItemNodeElements,
                index
            )

            if (
                updatedElementList &&
                updatedElementList?.contentItem?.elements?.length > 0
            ) {
                saveUpdatedContentItemValues(updatedElementList, true)
            } else {
                return
            }
        }
        return
    }, [template, element])
    const [getFamilyChanges] = useLazyQuery(GetFamilyElementChanges, {
        displayName: 'GetFamilyChanges',
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setElementTrackedChanges(
                data?.getProjectProductFamily?.trackChanges
            )
        },
    })
    const [updateProjectContentItemElement] = useMutation(
        SetProjectContentItemElements(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: React.useCallback(
                (data) => {
                    //deleting the old dtvcontentitem to handle changes in ids
                    //for full view - no need to get updated DTV for entire family and all its product type ,  as we are not adding anything new in DTV
                    if (selectedTopic) {
                        const updateTC = setTimeout(() => {
                            getFamilyChanges({
                                variables: {
                                    input: {
                                        projectId,
                                        elementId: selectedTopic?.ppfId,
                                    },
                                    includeProductTypeInFamilyTrackChanges: false, // false when topic is selected from dashboard
                                },
                            })
                            clearTimeout(updateTC)
                        }, 200)
                    }
                    if (
                        (!selectedTopic &&
                            contentTeamFromStudioPage &&
                            selectedProjectNode?.elementType !==
                                'PRODUCTTYPE') ||
                        !contentTeamFromStudioPage
                    ) {
                        const refetchAPITask = setTimeout(() => {
                            handleRetrieveUpdatedDTV(
                                (data?.setProjectContentItemElements as any)
                                    ?.partType
                            )

                            clearTimeout(refetchAPITask)
                        }, 0)
                    }
                    if (!selectedTopic) {
                        retrieveUpdatedElementChanges()
                    }

                    if (!isPillAdded) {
                        const dataUpdateTask = setTimeout(() => {
                            const {
                                contentItemNodes,
                                dtv,
                                tabSelected,
                                elementIdOnEdit,
                            } = workspaceContents()
                            const updatedContentItems = getUpdatedItemsArray(
                                contentItemNodes ?? [],
                                data?.setProjectContentItemElements
                                    ?.contentItemNodes ??
                                    ([] as Partial<ContentItem>[]),
                                true,
                                false
                            )

                            contentItemWorker.postMessage({
                                dtv: dtv?.items ?? [],
                                selectedTab: WorkspaceTabTitles[
                                    tabSelected
                                ] as string,
                                workspaceContentItems: updatedContentItems,
                            })
                            contentItemWorker.onmessage = (
                                event: MessageEvent<ContentItem[]>
                            ) => {
                                setWorkspaceContentItems(
                                    updatedContentItems.concat(event.data)
                                )
                                setHasChanges(false)
                                if (template.id === elementIdOnEdit) {
                                    setElementIdOnEdit(undefined)
                                }
                            }
                            clearTimeout(dataUpdateTask)
                        }, 1000) // Don't decrease the timer. It won't update the workspace content real-time if decreased
                    } else {
                        // this function is added for creation of  new UOM , Pills or text Field.
                        // the above block is having a timeout which causes an issue in the   of template  updation.The above block  may be used for track changes.

                        isNewAddedOptionGroup(data)
                    }

                    setReactiveWorkspaceActions('default')

                    setHasError(false)

                    showGlobalSnackbar?.({
                        variables: {
                            message: 'Changes Saved Successfully',
                            variant: 'success',
                            id: `ContentItemElementText-UpdateProjectContentItemSuccess`,
                        },
                    })
                    //This will be called once the UOM is created
                    if (fieldState.type === 'newUOM') {
                        setUomGrouping()
                    }

                    // After UI is updated, check if the content is a best practice
                    const isBP = !!template?.contentItem?.isBestPractice
                    const isGlobalBP = !!template?.contentItem
                        ?.isGlobalBestPractice
                    // If this content is related to best practices, check sync status
                    if (isBP || isGlobalBP) {
                        setTimeout(() => {
                            getBestPracticeElementData({
                                variables: {
                                    parentId: template?.contentItem?.id,
                                    projectId: projectData?.getProject?.id,
                                },
                            })
                        }, 300)
                    }
                },
                [
                    template,
                    handleRetrieveUpdatedDTV,
                    retrieveUpdatedElementChanges,
                    fieldState,
                    setProperty,
                    setWorkspaceContentItems,
                    selectedTopic,
                ]
            ),
            onError: (error) => {
                setReactiveWorkspaceActions('default')
                if (error.networkError) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'There was a problem saving your changes. Please copy your recent edits and reconnect to the network.',
                            variant: 'error',
                            id:
                                'WorkspacePage_SetProjectContentItemElementError',
                        },
                    })
                } else if (error.message.includes('character')) {
                    showGlobalSnackbar({
                        variables: {
                            message: `There was a problem saving your changes. Please remove "|" to save your changes.`,
                            variant: 'error',
                            id:
                                'WorkspacePage_SetProjectContentItemElementError',
                        },
                    })
                    if (editorRef) {
                        ;(editorRef as HTMLElement).focus()
                        setHasError(true)
                    }
                } else {
                    showGlobalSnackbar({
                        variables: {
                            message: `Changes you made could not be saved. Use the Escape (Esc) key to revert changes.`,
                            variant: 'error',
                            id:
                                'WorkspacePage_SetProjectContentItemElementError',
                        },
                    })
                    if (editorRef) {
                        ;(editorRef as HTMLElement).focus()
                        setHasError(true)
                    }
                }
            },
        }
    )

    const [
        getBestPracticeElementData,
        { data: bestPracticeElementData, loading: bestPracticeElementLoading },
    ] = useLazyQuery<BestPracticeDataResponseExtended>(BestPracticeQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'BestPracticeElement',
        onCompleted: (data) => {
            if (
                data?.getBestPractice &&
                checkIfBestPracticeIsOutOfSync &&
                processEnv.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                checkIfBestPracticeIsOutOfSync(data.getBestPractice)
            }
        },
        onError: (err) => {
            console.log(err)
        },
    })

    // this function updates workspace content item in real time
    const updateContentItemServiceWorker = React.useCallback((item) => {
        const { contentItemNodes } = workspaceContents()
        const updatedContentItems = getUpdatedItemsArray(
            contentItemNodes ?? [],
            [item],
            true,
            false
        )

        contentItemWorker.postMessage({
            workspaceContentItems: updatedContentItems as ContentItem[],
        })
        contentItemWorker.onmessage = (event: MessageEvent<ContentItem[]>) => {
            setWorkspaceContentItems(updatedContentItems.concat(event.data))
        }
    }, [])

    const [setAddUpdateBestPractice] = useMutation<
        AddUpdateBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(AddUpdateBestPracticeMutation, {
        onCompleted: React.useCallback(
            (data: AddUpdateBestPracticeResponse) => {
                if (data && data.addUpdateBestPractice) {
                    if (selectedPillElementId) {
                        handleBPfieldChange(
                            getIsBP(data.addUpdateBestPractice),
                            data.addUpdateBestPractice?.isGlobal ?? false
                        )

                        setSelectedPillElementId(null)
                    } else {
                        const item = {
                            ...template?.contentItem,
                            isBestPractice: getIsBP(data.addUpdateBestPractice),
                            isGlobalBestPractice:
                                data.addUpdateBestPractice?.isGlobal ?? false,
                        }
                        updateContentItemServiceWorker?.(item)

                        // After UI is updated, check if the content is a best practice

                        const isBP = item
                            ? !!item?.isBestPractice
                            : !!template?.contentItem?.isBestPractice
                        const isGlobalBP = item
                            ? !!item?.isGlobalBestPractice
                            : !!template?.contentItem?.isGlobalBestPractice
                        // If this content is related to best practices, check sync status
                        if (isBP || isGlobalBP) {
                            setTimeout(() => {
                                getBestPracticeElementData({
                                    variables: {
                                        parentId: template?.contentItem?.id,
                                        projectId: projectData?.getProject?.id,
                                    },
                                })
                            }, 300)
                        }
                    }
                }
            },
            [selectedPillElementId, template]
        ),
    })

    const [setRemoveBestPractice] = useMutation<
        RemoveBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(RemoveBestPracticeMutation, {
        onCompleted: React.useCallback(
            (data: RemoveBestPracticeResponse) => {
                if (data && data.removeBestPractice) {
                    if (selectedPillElementId) {
                        handleBPfieldChange(false, false)
                    } else {
                        const item = {
                            ...template?.contentItem,
                            isBestPractice: false,
                            isGlobalBestPractice: false,
                        }
                        updateContentItemServiceWorker?.(item)
                    }
                }
            },
            [selectedPillElementId]
        ),
    })

    const handleRightClick = React.useCallback(
        (
            event:
                | React.MouseEvent<HTMLButtonElement, MouseEvent>
                | React.MouseEvent<HTMLDivElement, MouseEvent>,
            elementId: string | undefined,
            isBestPractice: boolean,
            isGlobalBestPractice: boolean
        ) => {
            if ((selectedProjectNode as WorkspaceProjectElementNode)?.isFromPPLTemplate) return
            if (selectedParagraphItemId === selectedItemIdForPills) return
            setSelectedPillIsBP(isBestPractice)
            setSelectedPillIsGBP(isGlobalBestPractice)
            setPopoverAnchor(event.currentTarget)
            elementId &&
                fieldState.type !== 'Text' &&
                setSelectedPillElementId(elementId) // This is only set for option group items
            elementId &&
                getBestPracticeElementData({
                    variables: {
                        parentId: elementId,
                        projectId: projectData?.getProject.id,
                    },
                })
        },
        [
            selectedProjectNode,
            selectedParagraphItemId,
            selectedItemIdForPills,
            setPopoverAnchor,
            setSelectedPillIsBP,
            setSelectedPillIsGBP,
            setSelectedPillElementId,
            getBestPracticeElementData,
            projectData,
        ]
    )
    const handleChangepillInUom = React.useCallback(
        (newValue, type, index) => {
            if (type === 'text') {
                //work around  to fix the copy/paste html content
                const sanitizedNewVal = sanitizeHtml(
                    newValue.replace(/(?:^(?:&nbsp;)+)/g, ''),
                    {
                        allowedTags: [],
                    }
                )
                    ?.replace(/&lt;/g, '<')
                    ?.replace(/&gt;/g, '>')
                    ?.replace(/&amp;/g, '&')
                    ?.replace(/&quot;/g, '"')
                fieldState.elements![index!].text = !!sanitizedNewVal?.length
                    ? sanitizedNewVal
                    : ''
            } else if (type === 'metric') {
                fieldState.elements![index!].metricUnits = sanitizeHtml(
                    newValue
                )
            } else {
                fieldState.elements![index!].imperialUnits = sanitizeHtml(
                    newValue
                )
            }

            bodItem?.id === fieldState?.id &&
                setBodItem({ bodText: newValue, id: bodItem.id })
        },
        [fieldState, bodItem]
    )

    const handleChange = React.useCallback(
        (newValue: string) => {
            if (fieldState.type === 'newCreation') {
                if (hasChanges) {
                    return
                } else {
                    const updatedTemplate = getUpdatedTemplate(template, {
                        id: fieldState.id,
                        text: newValue,
                        placeholderText: fieldState.placeholderText,
                    })
                    onElementUpdate?.(updatedTemplate)
                }
            }
            if (fieldState?.type === 'ReplacementOption') {
                setFieldState({
                    ...fieldState,
                    changeType: 'MODIFIED',
                    text: newValue,
                })
                const updatedTemplate = getUpdatedTemplate(template, {
                    id: fieldState.id,
                    text: newValue,
                    placeholderText: fieldState.placeholderText,
                })

                onElementUpdate?.(updatedTemplate)
            } else if (showImperial && !showMetric) {
                setFieldState({
                    ...fieldState,
                    changeType: 'MODIFIED',
                    imperialUnits: formatSpace(newValue),
                    text: formatSpace(newValue),
                })
            } else if (showMetric) {
                setFieldState({
                    ...fieldState,
                    changeType: 'MODIFIED',
                    metricUnits: formatSpace(newValue),
                    text: formatSpace(newValue),
                })
            } else {
                setFieldState({
                    ...fieldState,
                    changeType: 'MODIFIED',
                    text: newValue,
                })
            }
            bodItem?.id === fieldState?.id &&
                setBodItem({ bodText: newValue, id: bodItem.id })

            setHasChanges(true)
        },
        [
            template,
            element,
            onElementUpdate,
            fieldState,
            bodItem,
            showImperial,
            showMetric,
        ]
    )

    const saveUpdatedContentItemValues = React.useCallback(
        (
            updatedTemplate: NormalizedDocumentTemplateViewItem,
            isCreatingPill?: boolean,
            isResetAllOptions?: Boolean
        ) => {
            if (hasError) {
                return
            }

            const isDeletingWholeParagraph = getIsDeletingWholeParagraph(
                updatedTemplate.contentItem?.elements
            )

            const editingInput = getPropertyEditingDetails({
                property: updatedTemplate,
                isForArticle: false,
                nodeId: selectedTopic
                    ? selectedTopic?.projectElementNodeId ?? ''
                    : selectedProjectNode?.id ?? '',
                ...getParentDetails(),
                selectedTopic,
                projectId,
            })
            const isSustainabilityTopic = type === 'Sustainability Topic'
            setElementIdOnEdit(updatedTemplate.id)
            const { tabSelected, projectProductFamilyId } = workspaceContents()

            if (!!productTypeNodeInFamily?.id) {
                // Set node Id for correct track changes update
                editingInput.nodeId = productTypeNodeInFamily.id
            }

            updateProjectContentItemElement({
                variables: {
                    input: {
                        ...editingInput,
                        isFromStudioManagementModule: isContentTeamUser,
                        elements: !!isDeletingWholeParagraph
                            ? // to delete the paragraph that has only new pill or new text field
                              [
                                  {
                                      ...updatedTemplate.contentItem
                                          ?.elements?.[0],
                                      changeType: TrackChangeType.DELETED,
                                      text: '',
                                  },
                              ]
                            : (makeElementsApiReady(
                                  updatedTemplate.contentItem?.elements
                              )?.map(
                                  (
                                      elementIterator: Partial<
                                          TextContentElement
                                      >
                                  ) => {
                                      const {
                                          originalText,
                                          isPremiumPlus: isManufacturerOption,
                                          ...apiReadyElementIterator
                                      }: any = elementIterator
                                      const {
                                          isOptionItem,
                                          fieldId,
                                          isBestPractice,
                                          isGlobalBestPractice,
                                          isPremiumPlus,
                                          parentOptionGroupId,
                                          __typename,
                                          ...apiPilInput
                                      }: any = elementIterator

                                      return isCreatingPill
                                          ? {
                                                ...apiPilInput,
                                                elements: apiPilInput?.elements?.map(
                                                    (elementsApi: any) => {
                                                        const {
                                                            isOptionItem,
                                                            fieldId,
                                                            isBestPractice,
                                                            isGlobalBestPractice,
                                                            isPremiumPlus,
                                                            originalText,
                                                            parentOptionGroupId,
                                                            __typename,
                                                            ...apiElementsInput
                                                        }: any = elementsApi

                                                        return {
                                                            ...apiPilInput,
                                                            ...apiElementsInput,
                                                            elements: apiElementsInput?.elements?.map(
                                                                (
                                                                    elementsApi: any
                                                                ) => {
                                                                    const {
                                                                        isOptionItem,
                                                                        fieldId,
                                                                        isBestPractice,
                                                                        isGlobalBestPractice,
                                                                        isPremiumPlus,
                                                                        originalText,
                                                                        parentOptionGroupId,
                                                                        __typename,
                                                                        ...apiElements
                                                                    }: any = elementsApi

                                                                    return apiElements
                                                                }
                                                            ),
                                                        }
                                                    }
                                                ),
                                            }
                                          : elementIterator.type ===
                                            'OptionGroup'
                                          ? {
                                                ...apiReadyElementIterator,
                                                elements: elementIterator?.elements?.map(
                                                    (
                                                        childElementIterator: Partial<
                                                            ChildTextContentElement
                                                        >
                                                    ) => {
                                                        const {
                                                            parentOptionGroupId,
                                                            originalText,
                                                            isPremiumPlus,
                                                            ...childElement
                                                        }: any = childElementIterator

                                                        const newElements = childElement.elements?.map(
                                                            (
                                                                element: ChildTextContentElement
                                                            ) => {
                                                                const {
                                                                    isPremiumPlus,
                                                                    ...newElement
                                                                } = element
                                                                return newElement
                                                            }
                                                        )
                                                        return childElement.type ===
                                                            'SelectionOption' ||
                                                            childElement.type ===
                                                                'ReplacementOption'
                                                            ? {
                                                                  ...childElement,
                                                                  changeType:
                                                                      childElement.changeType &&
                                                                      (isContentTeamUser ||
                                                                          isOMUser) &&
                                                                      contentTeamFromStudioPage
                                                                          ? TrackChangeType[
                                                                                childElement.changeType.toUpperCase() as 'ADDED'
                                                                            ]
                                                                          : TrackChangeStatusMap[
                                                                                childElement.optionItemState as keyof typeof TrackChangeStatusMap
                                                                            ],
                                                                  elements: newElements,

                                                                  isListingActivatedOption:
                                                                      childElement.type ===
                                                                      'SelectionOption'
                                                                          ? childElement?.optionItemState ===
                                                                            'OptedIn'
                                                                              ? false
                                                                              : null
                                                                          : undefined,
                                                              }
                                                            : {
                                                                  ...childElement,
                                                                  elements: newElements,
                                                              }
                                                    }
                                                ),
                                            }
                                          : apiReadyElementIterator
                                  }
                              ) as Partial<TextContentElementInput>[]),
                        isMasterSpecCloneNode:
                            selectedProjectNode?.isMasterSpecClone,
                        iterationId: selectedProjectNode?.iteration?.id,
                        selectedPillElementId: fieldState?.id,
                        isDeletingWholeParagraph: !!isDeletingWholeParagraph,
                        isDeletingBaseContent: !template?.isUserAdded,
                    },
                    partType: isSustainabilityTopic
                        ? 'CONTENTGROUP'
                        : contentTeamFromStudioPage && typeForFullView
                        ? typeForFullView
                        : WorkspaceTabTitles[tabSelected ?? 0],
                    projectId,
                    rootArticleContentItemIds: [
                        updatedTemplate.articleId ?? '',
                    ],
                    productFamilyId: isSustainabilityTopic
                        ? selectedTopic?.ppfId
                        : projectProductFamilyId,
                    isTrackChangeToggleOn,
                    isResetAllOptions,
                },
            })
            if (selectedTopic) return
            if (
                element.type === 'SelectionOption' ||
                element.type === 'ReplacementOption'
            ) {
                handleChangeParagraphItemId?.('')
                handleChangeParagraphActionState?.('')
            }
            if (
                updatedTemplate.isBOD &&
                updatedTemplate?.contentItem?.elements
                    ?.find((element) => element?.type === 'OptionGroup')
                    ?.elements?.find(
                        (bodElement) =>
                            bodElement?.type === 'ReplacementOption' &&
                            bodElement?.text ===
                                "Insert manufacturer's name; product name or designation"
                    )
            ) {
                serviceWorker?.controller?.postMessage({
                    [WORKSPACE_MANUFACTURER_SELECTED_KEY]: ({
                        listingId: '',
                        isSelected: false,
                        isBOD: true,
                        deleteBODListing: true,
                    } as unknown) as WorkspaceManufacturerSelected,
                })
            }
        },
        [
            template,
            updateProjectContentItemElement,
            selectedProjectNode,
            serviceWorker,
            getParentDetails,
            projectId,
            selectedTopic,
            isSustainabilityTopic,
            isTrackChangeToggleOn,
            typeForFullView,
            contentTeamFromStudioPage,
            productTypeNodeInFamily,
            fieldState,
        ]
    )

    const debouncedUpdateSelectionOptionItemState = React.useCallback(
        (
            newValue: Partial<TextContentElementInput>,
            newTemplate?: NormalizedDocumentTemplateViewItem,
            multipleSelectionType?: string
        ) => {
            const isResetAllOptions = multipleSelectionType !== undefined
            const updatedTemplate = getUpdatedTemplate(
                newTemplate ? newTemplate : template,
                newValue,
                multipleSelectionType,
                getParentContentId(template, fieldState) ?? undefined
            )

            handleUpdateTemplate?.(updatedTemplate)
            saveUpdatedContentItemValues(
                updatedTemplate,
                false,
                isResetAllOptions
            )
        },
        [template, handleUpdateTemplate, saveUpdatedContentItemValues]
    )

    const { insertUomPill } = useInsertUomPill(
        setFieldState,
        fieldState,
        template
    )

    const handleNewUOMErrorNotification = useCallback(
        (hasUOMError?: boolean) => {
            //This function is created to display an error message  while fields are blank

            showGlobalSnackbar?.({
                variables: {
                    message: 'Please Enter Required Fields',
                    variant: 'error',
                    id: 'createPillInfo',
                },
            })

            return true
        },
        [fieldState, showGlobalSnackbar]
    )
    const onBlurInput = useCallback(
        (
            values?: string,
            type?: string,
            event?: React.FocusEvent<
                HTMLTextAreaElement | HTMLInputElement | HTMLSpanElement
            >
        ) => {
            let totalElements = fieldState.elements?.length!! - 2

            if (type === 'uomInPill') {
                if (fieldState?.elements) {
                    const isMetricEmpty =
                        fieldState?.elements?.[totalElements]?.metricUnits ===
                            '' ||
                        fieldState?.elements?.[totalElements]?.metricUnits ===
                            undefined
                    const isImperialEmpty =
                        fieldState?.elements?.[totalElements]?.imperialUnits ===
                            '' ||
                        fieldState?.elements?.[totalElements]?.imperialUnits ===
                            undefined
                    if (isImperialEmpty || isMetricEmpty) {
                        handleNewUOMErrorNotification()
                    } else {
                        const updatedTemp = getUpdatedUOMInPillTemplate(
                            template,
                            fieldState,
                            getParentContentId(template, fieldState) ??
                                undefined,
                            fieldState,
                            'Blur'
                        )

                        handleUpdateTemplate?.(updatedTemp)

                        setIsPillAdded(true)
                        const isCreatedAtLast =
                            updatedTemp?.contentItem?.elements?.findIndex(
                                (item) =>
                                    item?.type === 'OptionGroup' &&
                                    item?.elements?.[0]?.id === fieldState.id
                            ) ===
                            updatedTemp?.contentItem?.elements?.length! - 1

                        saveUpdatedContentItemValues(
                            addNewElementAtLastInTemplate(
                                isCreatedAtLast,
                                updatedTemp
                            ),
                            true
                        )
                        fieldState.type = 'SelectionOption'
                    }
                } else if (
                    ((event?.target as unknown) as HTMLSpanElement)?.id?.includes(
                        'uomInPill'
                    )
                ) {
                    event?.target.focus()
                }
            } else if (fieldState.type === 'newPill') {
                if (values?.trim() !== '') {
                    const updatedTemplate = getUpdatedTemplateAfterPillAdd({
                        template,
                        elementObjValue: {
                            text: values,
                            placeholderText: null,
                            elements: template?.contentItem?.elements,
                        },
                        multipleSelectionType: TextVariants.SelectionOption,
                        fieldState: undefined,
                        id:
                            getParentContentId(template, fieldState) ??
                            undefined,
                        values,
                        type: TextVariants.SelectionOption,
                    })
                    if (!checkPillWithText) {
                        //This will  update the template if user creates pill only

                        handleUpdateTemplate?.(updatedTemplate)
                    }
                    setIsPillAdded(true)
                    saveUpdatedContentItemValues(updatedTemplate, true)
                    setFieldState(fieldState)
                } else {
                    // Need to check using ref to know that dialog is open
                    // Don't re-render when dialog is open, so that it doesn't close as soon as it opens
                    if (!isPillDeleteDialogVisible.current) {
                        // This causes the re-render
                        handleDeleteOption()
                    }
                }
            }
        },
        [
            fieldState,
            editable,
            template,
            isPillAdded,
            checkPillWithText,
            getParentContentId,
            handleUpdateTemplate,
            saveUpdatedContentItemValues,
            handleNewUOMErrorNotification,
            isPillDeleteDialogVisible,
        ]
    )

    const displayPillDeleteDialog = React.useCallback(
        (showModal: boolean) => {
            setShowPillDeleteDialog(showModal)
            isPillDeleteDialogVisible.current = showModal
        },
        [setShowPillDeleteDialog, isPillDeleteDialogVisible]
    )

    const updateElementOptionState = React.useCallback(
        (
            parentContentItemId: string,
            template: NormalizedDocumentTemplateViewItem,
            revert?: boolean,
            multiplePillUpdateType?: string
        ) => {
            const isReplacementOption = fieldState.type === 'ReplacementOption'
            const clonedTemplate = cloneDeep(template)
            const bodItemClonned = clonedTemplate?.contentItem?.elements
                ? findBODElementFromContent(
                      clonedTemplate?.contentItem?.elements
                  )
                : ''
            const parentElementIndex = clonedTemplate?.contentItem?.elements?.findIndex(
                (el) => {
                    return el.id === parentContentItemId
                }
            )
            if (parentElementIndex !== undefined) {
                const updatedOptionElements = clonedTemplate?.contentItem?.elements?.[
                    parentElementIndex
                ]?.elements?.map((el: TextContentElementInput) => {
                    switch (el.type) {
                        case 'SelectionOption':
                            return {
                                ...el,
                                placeholderText: el.text,
                                optionItemState: multiplePillUpdateType
                                    ? multiplePillUpdateType
                                    : el.optionItemState,
                                changeType: 'MODIFIED',
                            } as Partial<ChildTextContentElement>
                        case 'ReplacementOption': {
                            if (el?.optionItemState === 'InActive') {
                                return {
                                    ...el,
                                    optionItemState: 'InActive',
                                    changeType: 'MODIFIED',
                                } as Partial<ChildTextContentElement>
                            } else if (
                                multiplePillUpdateType !== undefined &&
                                multiplePillUpdateType !== ''
                            ) {
                                return {
                                    ...el,
                                    optionItemState: multiplePillUpdateType,
                                    text:
                                        isReplacementOption ||
                                        el.text === bodItemClonned
                                            ? el.text
                                            : el.placeholderText,
                                    changeType: 'MODIFIED',
                                } as Partial<ChildTextContentElement>
                            } else {
                                return {
                                    ...el,
                                    text:
                                        isReplacementOption ||
                                        el.text === bodItemClonned
                                            ? el.text
                                            : el.placeholderText,
                                    optionItemState:
                                        el.text === el.placeholderText
                                            ? 'OptedOut'
                                            : isReplacementOption
                                            ? revert
                                                ? 'OptedOut'
                                                : 'OptedIn'
                                            : el.optionItemState,
                                    changeType: 'MODIFIED',
                                } as Partial<ChildTextContentElement>
                            }
                        }
                        default:
                            return el as Partial<ChildTextContentElement>
                    }
                })
                const updatedElements =
                    clonedTemplate?.contentItem?.elements ?? []
                if (updatedElements[parentElementIndex] === undefined)
                    return template
                updatedElements[
                    parentElementIndex
                ].elements = updatedOptionElements

                const returnUpdatedTemplate = {
                    ...clonedTemplate,
                    contentItem: {
                        ...clonedTemplate.contentItem,
                        elements: updatedElements,
                    },
                }

                return returnUpdatedTemplate as NormalizedDocumentTemplateViewItem
            }
            return template
        },
        [fieldState]
    )

    const handleSelectionOptionChange = React.useCallback(
        (currentlySelected: boolean, multipleSelectionType?: string) => {
            const isActiveReplacementOption =
                fieldState?.type === 'ReplacementOption' && currentlySelected
            const disablePillSelection =
                (fieldState?.type === 'SelectionOption' ||
                    isActiveReplacementOption) &&
                contentTeamFromStudioPage
            if (!editable || disablePillSelection) return

            const isValidFieldState =
                fieldState?.type === 'SelectionOption' ||
                fieldState?.type === 'OptionGroup' ||
                fieldState?.type === 'ReplacementOption'

            const newValue =
                multipleSelectionType === 'OptedOut'
                    ? 'OptedOut'
                    : !currentlySelected
                    ? 'OptedIn'
                    : isValidFieldState
                    ? 'InActive'
                    : 'OptedOut'

            let updatedValue = {
                id: fieldState.id,
                optionItemState: newValue,
                text: fieldState.text,
                parentOptionGroupId:
                    getParentContentId(template, fieldState) ?? undefined,
                changeType: 'MODIFIED',
                isBestPractice: fieldState.isBestPractice,
                isGlobalBestPractice: fieldState.isGlobalBestPractice,
            }
            if (
                (fieldState.type === 'ReplacementOption' ||
                    fieldState.type === 'SelectionOption') &&
                !multipleSelectionType
            ) {
                handleOnFocus()
            }
            if (
                multipleSelectionType !== undefined &&
                multipleSelectionType !== '' &&
                selectedParagraphActionState !== '' &&
                selectedParagraphItemId === selectedItemIdForPills
            ) {
                debouncedUpdateSelectionOptionItemState(
                    {
                        id: fieldState.id,
                        optionItemState: multipleSelectionType,
                        text: fieldState?.text,
                    },
                    undefined,
                    multipleSelectionType ?? ''
                )

                setFieldState({
                    ...fieldState,
                    optionItemState: multipleSelectionType as OptionItemState,
                })
            } else if (
                fieldState.type === 'ReplacementOption' &&
                currentlySelected
            ) {
                let updatedText = fieldState.text
                const hasElements = element.elements?.length! > 0
                if (hasElements) {
                    updatedText = getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure,
                        true
                    )
                }
                setFieldState({
                    ...fieldState,
                    optionItemState: newValue,
                    text: updatedText,
                    changeType: 'MODIFIED',
                })
                debouncedUpdateSelectionOptionItemState({
                    id: fieldState.id,
                    optionItemState: newValue,
                    text: updatedText,
                    changeType: 'MODIFIED',
                } as any)
            } else {
                if (!currentlySelected) {
                    updatedValue = {
                        ...updatedValue,
                        optionItemState:
                            fieldState?.optionItemState === 'InActive' &&
                            fieldState.type === 'ReplacementOption'
                                ? fieldState?.text ===
                                  fieldState?.placeholderText
                                    ? 'OptedOut'
                                    : 'OptedIn'
                                : newValue,
                        parentOptionGroupId:
                            getParentContentId(template, fieldState) ??
                            undefined,
                        changeType: 'MODIFIED',
                    }
                } else {
                    updatedValue = {
                        ...updatedValue, // Added this to get the current BP values of the pill
                        id: fieldState.id,
                        optionItemState: newValue,
                        changeType: 'MODIFIED',
                    } as any
                }
                debouncedUpdateSelectionOptionItemState(updatedValue)

                setFieldState({
                    ...fieldState,
                    optionItemState: newValue,
                })
            }
        },
        [
            debouncedUpdateSelectionOptionItemState,
            fieldState,
            editable,
            template,
            element,
            showImperial,
            showMetric,
            unitsOfMeasure,
            handleUpdateTemplate,
            saveUpdatedContentItemValues,
            updateElementOptionState,
            selectedParagraphItemId,
            selectedItemIdForPills,
            selectedParagraphActionState,
        ]
    )

    const handleDeleteOption = React.useCallback(() => {
        // return if the content is not editable
        if (!editable) return

        let updatedValue = {
            id: fieldState.id,
            text: fieldState.text,
        }
        if (
            fieldState.type === 'SelectionOption' ||
            fieldState.type === 'ReplacementOption' ||
            fieldState.type === 'newPill' ||
            fieldState.type === 'newReplacement' ||
            fieldState.type === 'UnitsOfMeasure' ||
            fieldState.type === 'newUOM'
        ) {
            const updatedTemplate = getUpdatedTemplateAfterPillRemove(
                template,
                updatedValue,
                kpUserId
            )
            handleUpdateTemplate?.(updatedTemplate)

            saveUpdatedContentItemValues(updatedTemplate)

            setFieldState({
                ...fieldState,
            })
        }
    }, [
        fieldState,
        editable,
        template,
        handleUpdateTemplate,
        saveUpdatedContentItemValues,
    ])

    const getNonSharedSiblings = React.useCallback(
        (template: NormalizedDocumentTemplateViewItem) => {
            return template?.documentTemplateView?.items
                ?.filter(
                    (item: Partial<DocumentTemplateViewItem>) =>
                        item?.parentId === template?.parentId &&
                        !item?.documentTemplateViewContents?.[0]
                            ?.documentTemplateViewContentItems?.[0]
                            ?.isSharedProperty
                )
                ?.sort((a, b) => (a?.order || 0) - (b?.order || 0))
        },
        []
    )

    /*It is used to validate the Move Down action in the Workspace Action Bar if the paragraph is not valid to move down because it is the last item*/
    const isTemplateLastItem = React.useCallback(() => {
        let isTemplateDataLastItem = template?.isLastSibling
        if (!areSharedVisible) {
            const notSharedSiblings = getNonSharedSiblings(template)
            const currContentIndex = notSharedSiblings?.findIndex(
                (item) => item?.itemId === template?.itemId
            )
            isTemplateDataLastItem = !!(
                currContentIndex ===
                notSharedSiblings?.length! - 1
            )
        }

        setIsTemplateDataLastItem({ isTemplateDataLastItem })
    }, [workspaceTemplateData, setIsTemplateDataLastItem, areSharedVisible])

    const handleOnFocus = React.useCallback(() => {
        let currentTemplate = template
        if (
            template?.isSustainabilityContentItem &&
            (!template?.children || template?.children?.length === 0)
        ) {
            currentTemplate =
                parentTemplate?.children?.find(
                    (item) =>
                        item?.itemId === template?.parentId ||
                        item?.contentItem?.id === template?.parentId
                ) ?? template
        }

        setPromoteDemoteWorkspaceTemplateData({
            promoteDemoteWorkspaceTemplateData: {
                ...currentTemplate,
                elementId: fieldState?.id,
            },
        })
        isTemplateLastItem()
        //only set the workspaceTemplateData when the element is not newly added because we only want to enable move up and down when a template is saved
        if (fieldState.type !== 'NewlyAdded') {
            setWorkspaceTemplateData({ workspaceTemplateData: currentTemplate })
        }

        if (fieldState.type !== 'NewlyAdded' && !!element?.changeType) {
            setPropertyToFocus(element.id, template?.id)
        }

        if (propertyToFocus && !element?.changeType) {
            resetOnFocusProperty(false)
        }
    }, [
        element,
        propertyToFocus,
        setPropertyToFocus,
        fieldState,
        template,
        isTemplateLastItem,
        workspaceTemplateData,
    ])

    const handleMouseLeave = React.useCallback(() => {
        resetOnFocusProperty()
    }, [setPropertyToFocus])

    const handleSavingTextChanges = React.useCallback(
        (
            updatedElementList: TextContentElement[],
            nonFormattedText: string,

            indexOfPillForNewPt?: number,
            typeOfPillForNewPt?: string,
            isPillWithNewPara?: boolean
        ) => {
            if (element.type === 'NewlyAdded') {
                // Validate for duplicate PT keys before saving newly added property
                if (nonFormattedText?.includes(':')) {
                    const newKey = nonFormattedText.substring(
                        0,
                        nonFormattedText.indexOf(':')
                    )

                    // For newly added properties, get siblings from the parent
                    const parentDetails = getParentDetails()
                    const siblingIds = getSiblings(
                        parentDetails.contentItemParentId
                    )

                    // Remove trailing pipe from parentKey if it exists
                    const cleanParentKey = parentDetails.parentKey?.endsWith('|')
                        ? parentDetails.parentKey.slice(0, -1)
                        : parentDetails.parentKey

                    const isKeyError = handleEditKeyChanges(
                        template?.contentItem?.textType ?? '',
                        `|${cleanParentKey}|${newKey}`,
                        siblingIds
                    )

                    if (isKeyError) {
                        showGlobalSnackbar({
                            variables: {
                                message: KeyErrorMessage,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                        setHasError(true)
                        if (editorRef) {
                            ;(editorRef as HTMLInputElement).focus()
                        }

                        return
                    }
                }

                if (
                    typeOfPillForNewPt &&
                    indexOfPillForNewPt!! >= 0 &&
                    contentTeamFromStudioPage
                ) {
                    //Here we are  sendimg the position and type of pill which
                    saveNewlyAddedProperty(
                        nonFormattedText as string,

                        indexOfPillForNewPt,
                        typeOfPillForNewPt,
                        isPillWithNewPara
                    )
                } else {
                    saveNewlyAddedProperty(nonFormattedText as string)
                }
                setHasChanges(false)
            } else if (
                UpdateableTypes.includes(fieldState.type ?? '') &&
                (template.parentType === 'Nugget' ||
                    template.parentType === 'ProductTypeProperty') &&
                element.type !== 'NewlyAdded'
            ) {
                const reducedUpdatedElements = template?.contentItem?.elements?.reduce(
                    (prevElementList, originalElement) => {
                        if (originalElement.id === element.id) {
                            return [
                                ...prevElementList,
                                ...updatedElementList.map((element) => {
                                    // strip fields that don't exist on the API input type, like isOptionItem
                                    const {
                                        isOptionItem,
                                        fieldId,
                                        isBestPractice,
                                        isGlobalBestPractice,
                                        __typename,
                                        ...apiReadyElement
                                    }: any = element
                                    return apiReadyElement
                                }),
                            ]
                        } else {
                            const {
                                isOptionItem,
                                fieldId,
                                isBestPractice,
                                isGlobalBestPractice,
                                __typename,
                                ...apiReadyOriginalElement
                            }: any = originalElement

                            return [
                                ...prevElementList,
                                {
                                    ...apiReadyOriginalElement,
                                    elements: apiReadyOriginalElement.elements?.map(
                                        (childElement: TextContentElement) => {
                                            const {
                                                isOptionItem,
                                                fieldId,
                                                isBestPractice,
                                                isGlobalBestPractice,
                                                __typename,
                                                ...apiReadyChildElement
                                            }: any = childElement

                                            return {
                                                ...apiReadyChildElement,
                                                elements: apiReadyChildElement.elements?.map(
                                                    (
                                                        grandchildElement: TextContentElement
                                                    ) => {
                                                        const {
                                                            isOptionItem,
                                                            fieldId,
                                                            isBestPractice,
                                                            isGlobalBestPractice,
                                                            __typename,
                                                            ...apiReadyGrandchildElement
                                                        }: any = grandchildElement
                                                        return apiReadyGrandchildElement
                                                    }
                                                ),
                                            }
                                        }
                                    ),
                                },
                            ]
                        }
                    },
                    [] as Partial<TextContentElement>[]
                )

                if (
                    element.id === '' &&
                    element.text === ' ' &&
                    element.type === 'Text'
                ) {
                    reducedUpdatedElements?.unshift({
                        ...updatedElementList[0],
                    })
                }
                const updatedTemplate = {
                    ...template,
                    contentItem: {
                        ...template?.contentItem,
                        elements: reducedUpdatedElements,
                    },
                }

                let modifiedKey = ''

                reducedUpdatedElements?.map((item) => {
                    if (
                        item.changeType?.toLowerCase() !== 'deleted' &&
                        item.type === 'Text'
                    ) {
                        modifiedKey = modifiedKey + item.text
                    }
                })

                if (modifiedKey?.includes(':')) {
                    const isKeyError = handleEditKeyChanges(
                        template?.contentItem?.textType ?? '',
                        `|${template.parentKey}|${modifiedKey.substring(
                            0,
                            modifiedKey.indexOf(':')
                        )}`,
                        getSiblings(template?.contentItem?.id as string)
                    )

                    if (isKeyError) {
                        showGlobalSnackbar({
                            variables: {
                                message: KeyErrorMessage,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                        setHasError(true)
                        if (!!editorRef) {
                            ;(editorRef as HTMLInputElement).focus()
                        }

                        return
                    }
                }

                const fieldStateText = nonFormattedText

                if (fieldStateText?.includes(':') && template?.isUserAdded) {
                    const isKeyError = handleEditKeyChanges(
                        template?.contentItem?.textType ?? '',
                        `|${template.parentKey}|${fieldStateText.substring(
                            0,
                            fieldStateText.indexOf(':')
                        )}`,
                        getSiblings(template?.contentItem?.id as string)
                    )

                    if (isKeyError) {
                        showGlobalSnackbar({
                            variables: {
                                message: KeyErrorMessage,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                        setHasError(true)
                        if (editorRef) {
                            ;(editorRef as HTMLInputElement).focus()
                        }

                        return
                    }
                } else {
                    const isPropHasKey = template?.contentItem?.elements?.some(
                        (element: Partial<TextContentElement>) =>
                            element?.text?.includes(':')
                    )
                    let uniqueKey = fieldStateText?.includes(':')
                        ? fieldStateText.split(':')?.[0]
                        : isPropHasKey
                        ? (template?.contentItem?.elements
                              ?.find((element: Partial<TextContentElement>) =>
                                  element?.text?.includes(':')
                              )
                              ?.text?.split(':')?.[0] as string)
                        : undefined

                    if (uniqueKey === '' && isPropHasKey) {
                        const keyIndicatorIndex = template?.contentItem?.elements?.findIndex(
                            (element: Partial<TextContentElement>) =>
                                element?.text?.includes(':')
                        )
                        if (keyIndicatorIndex && keyIndicatorIndex > -1) {
                            for (
                                var index = keyIndicatorIndex - 1;
                                index >= 0;
                                index--
                            ) {
                                if (
                                    template?.contentItem?.elements?.[index]
                                        ?.type === 'Text'
                                ) {
                                    uniqueKey =
                                        template?.contentItem?.elements[index]
                                            ?.text
                                    break
                                }
                            }
                        }
                    }
                    if (
                        !uniqueKey &&
                        (isParentProductGroup ||
                            template?.documentTemplateViewItem
                                ?.isProductGroup ||
                            manufacturerArticleIds?.some(
                                (item) => item === template?.id
                            ))
                    ) {
                        showGlobalSnackbar({
                            variables: {
                                message: ProductGroupKeyErrorMessage,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                        setEditError?.(true)
                        if (editorRef) {
                            ;(editorRef as HTMLInputElement).focus()
                        }

                        return
                    }
                }

                setEditError?.(false)
                let parentContentItemId = getParentContentId(
                    template,
                    updatedElementList[0]
                )
                const parentOptionGroupIndex = template?.contentItem?.elements?.findIndex(
                    (element: Partial<TextContentElement>) =>
                        element?.id?.includes?.(`${parentContentItemId}`)
                )
                if (
                    parentOptionGroupIndex!! > -1 &&
                    template?.contentItem?.elements?.[parentOptionGroupIndex!!]
                        .type === 'OptionGroup'
                ) {
                    //  If we are adding an  text in b/w optionGroup element

                    let elementInSideOptionGroup: any =
                        template?.contentItem?.elements?.[
                            parentOptionGroupIndex!!
                        ]

                    const childElementIndex = elementInSideOptionGroup?.elements?.findIndex(
                        (element: Partial<TextContentElement>) =>
                            element?.id?.includes?.(
                                `${updatedElementList[0].id}`
                            )
                    )
                    const isChangeTypeAdded = updatedElementList?.some(
                        (element: Partial<TextContentElement>) =>
                            element?.changeType?.includes('ADDED')
                    )
                    if (
                        isChangeTypeAdded &&
                        updatedElementList?.length > 0 &&
                        updatedElementList?.length <= 2
                    ) {
                        for (
                            let i = 0;
                            i <= elementInSideOptionGroup?.elements?.length;
                            i += 1
                        ) {
                            if (
                                elementInSideOptionGroup?.elements[i]?.id ===
                                updatedElementList[0]?.id
                            ) {
                                if (updatedElementList[0].text !== ' ') {
                                    elementInSideOptionGroup.elements[i] = {
                                        ...updatedElementList[0],
                                        imperialUnits: '',
                                        metricUnits: '',
                                        placeholderText: null,
                                        authoredUserId: '',
                                        optionItemState: null,
                                        changeType: 'Added',
                                        elements: [],
                                    }
                                } else {
                                    elementInSideOptionGroup.elements[i] = {
                                        ...updatedElementList[1],
                                        imperialUnits: '',
                                        metricUnits: '',
                                        placeholderText: null,
                                        authoredUserId: '',
                                        optionItemState: null,
                                        changeType: 'Added',
                                        elements: [],
                                    }
                                }
                            }
                        }
                    } else {
                        elementInSideOptionGroup?.elements?.splice(
                            childElementIndex,
                            1,
                            ...updatedElementList
                        )
                        template.contentItem!.elements![
                            parentOptionGroupIndex!
                        ] = elementInSideOptionGroup
                    }
                    template.contentItem!.elements![
                        parentOptionGroupIndex!
                    ] = elementInSideOptionGroup
                    handleUpdateTemplate?.(template)
                    saveUpdatedContentItemValues(template, true)
                    return
                }
                saveUpdatedContentItemValues(updatedTemplate)
                setHasChanges(true)
            }
        },
        [
            element,
            template,
            saveNewlyAddedProperty,
            saveUpdatedContentItemValues,
        ]
    )
    const handleBlur = React.useCallback(
        (
            event?: React.FocusEvent<
                HTMLTextAreaElement | HTMLInputElement | HTMLSpanElement
            >
        ) => {
            // return if the content is not editable
            if (!editable) return

            // If the element's value is unchanged,
            // or if the current element is not added to project,
            // don't do anything.
            const fieldStateText = fieldState.text
            if (
                (fieldState.type === 'newUOM' ||
                    fieldState.type === 'UnitsOfMeasure') &&
                contentTeamFromStudioPage
            ) {
                //This if case if for new UOM and already create UOM but in
                // SMM we have to save atleat of  field for UOM
                const newUOMElement = {
                    id: fieldState.id,
                    imperialUnits:
                        showImperial && !showMetric
                            ? formatSpace(fieldStateText)
                            : element.imperialUnits,
                    metricUnits: showMetric
                        ? formatSpace(fieldStateText)
                        : element.metricUnits,
                    text: formatSpace(fieldStateText),
                    placeholderText: fieldState.placeholderText,
                }
                let updateTem = getUpdatedTemplate(template, newUOMElement)
                onElementUpdate?.(updateTem)

                if (
                    !(event?.relatedTarget as HTMLSpanElement)?.id?.includes(
                        'replacementOption'
                    )
                ) {
                    // Process UOM only when Escape is not pressed
                    if (
                        newUOMElement.imperialUnits === '' &&
                        newUOMElement.metricUnits === '' &&
                        event?.relatedTarget === null
                    ) {
                        handleNewUOMErrorNotification()
                    } else {
                        if (
                            (newUOMElement.imperialUnits !== '' ||
                                newUOMElement.metricUnits !== '') &&
                            event?.relatedTarget === null
                        ) {
                            const update = getUpdatedTemplate(template, {
                                ...newUOMElement,
                                changeType: 'ADDED',
                                type: 'UnitsOfMeasure',
                            })
                            setIsPillAdded(true)
                            const isUOMCreatedAtLast =
                                updateTem?.contentItem?.elements?.findIndex(
                                    (item) => item?.id === newUOMElement.id
                                ) ===
                                updateTem?.contentItem?.elements?.length! - 1
                            saveUpdatedContentItemValues(
                                addNewElementAtLastInTemplate(
                                    isUOMCreatedAtLast,
                                    update
                                )
                            )
                        }
                    }
                }
                return
            }
            if (fieldState.type === 'newReplacement') {
                setHasChanges(true)
                if (fieldStateText?.trim() !== '') {
                    const updatedTemplate = getUpdatedTemplateAfterPillAdd({
                        template,
                        elementObjValue: {
                            ...fieldState,
                            text: fieldStateText,
                            placeholderText: null,
                            elements: template?.contentItem?.elements,
                        },
                        multipleSelectionType: TextVariants.ReplacementOption,
                        fieldState: undefined,
                        id:
                            getParentContentId(template, fieldState) ??
                            undefined,
                    })
                    setIsPillAdded(true)
                    if (!checkPillWithText) {
                        handleUpdateTemplate?.(updatedTemplate)
                    }
                    saveUpdatedContentItemValues(updatedTemplate, true)
                }
                return
            }
            if ((!hasChanges && fieldState.type !== 'NewlyAdded') || !editable)
                return
            else if (
                UpdateableTypes.includes(fieldState.type ?? '') &&
                (template.parentType === 'Nugget' ||
                    template.parentType === 'ProductTypeProperty')
            ) {
                if (
                    fieldState.type === 'ReplacementOption' &&
                    !template?.isBOD
                ) {
                    let isRevert = false
                    if (
                        fieldStateText !== '&nbsp;' &&
                        fieldStateText !== fieldState.placeholderText &&
                        fieldStateText?.trim() !== ''
                    ) {
                        const newObjValue = {
                            ...fieldState,
                            optionItemState: 'OptedIn',
                            text: fieldStateText,
                            placeholderText: fieldState.placeholderText,
                            changeType: 'MODIFIED',
                        }
                        const updatedTemplate = getUpdatedTemplate(
                            template,
                            newObjValue,
                            undefined,
                            getParentContentId(template, fieldState) ??
                                undefined
                        )
                        const parentId = getParentContentId(
                            updatedTemplate,
                            fieldState
                        )
                        const updatedOptionTemplate = parentId
                            ? updateElementOptionState(
                                  parentId,
                                  updatedTemplate,
                                  isRevert
                              )
                            : updatedTemplate
                        handleUpdateTemplate?.(updatedOptionTemplate)
                        saveUpdatedContentItemValues(updatedOptionTemplate)
                    } else {
                        isRevert = true
                        let updatedValue = {
                            id: fieldState.id,
                            optionItemState: 'OptedOut',
                            text: fieldState.placeholderText,
                            changeType: 'MODIFIED',
                        }
                        const updatedTemplate = getUpdatedTemplate(
                            template,
                            updatedValue,
                            undefined,
                            getParentContentId(template, fieldState) ??
                                undefined
                        )
                        const parentId = getParentContentId(
                            updatedTemplate,
                            fieldState
                        )
                        const updatedOptionTemplate = parentId
                            ? updateElementOptionState(
                                  parentId,
                                  updatedTemplate,
                                  isRevert
                              )
                            : updatedTemplate
                        handleUpdateTemplate?.(updatedOptionTemplate)
                    }
                } else if (
                    template?.isBOD &&
                    fieldState?.text?.trim().length === 0
                ) {
                    const newObjValue = {
                        id: fieldState.id,
                        optionItemState: null,
                        text: `Insert manufacturer's name; product name or designation`,
                    }
                    const updatedTemplate = getUpdatedTemplate(
                        template,
                        newObjValue,
                        undefined,
                        getParentContentId(template, fieldState) ?? undefined
                    )
                    setFieldState({
                        ...fieldState,
                        text: `Insert manufacturer's name; product name or designation`,
                    })
                    setBodItem({
                        id: bodItem?.id,
                        bodText: `Insert manufacturer's name; product name or designation`,
                    })
                    handleUpdateTemplate?.(updatedTemplate)
                    saveUpdatedContentItemValues(updatedTemplate)
                } else {
                    if (element.type !== 'NewlyAdded') {
                        // Validate for duplicate PT keys before saving edited content
                        if (fieldStateText?.includes(':')) {
                            const isKeyError = handleEditKeyChanges(
                                template?.contentItem?.textType ?? '',
                                `|${template.parentKey}|${fieldStateText.substring(
                                    0,
                                    fieldStateText.indexOf(':')
                                )}`,
                                getSiblings(template?.contentItem?.id as string)
                            )

                            if (isKeyError) {
                                showGlobalSnackbar({
                                    variables: {
                                        message: KeyErrorMessage,
                                        variant: 'error',
                                        id:
                                            'WorkspacePage_SetProjectContentItemElementError',
                                    },
                                })
                                setHasError(true)
                                if (editorRef) {
                                    ;(editorRef as HTMLInputElement).focus()
                                }

                                return
                            }
                        }

                        const newObjValue =
                            fieldState?.id === bodItem?.id
                                ? {
                                      ...fieldState,
                                      text: fieldStateText,
                                      optionItemState: 'OptedIn',
                                      placeholderText: fieldStateText,
                                      changeType: 'MODIFIED',
                                  }
                                : {
                                      ...fieldState,
                                      text: fieldStateText,
                                      placeholderText: fieldStateText,
                                      changeType: 'MODIFIED',
                                  }
                        const updatedTemplate = getUpdatedTemplate(
                            template,
                            newObjValue,
                            undefined,
                            getParentContentId(template, fieldState) ??
                                undefined
                        )
                        if (fieldState?.id === bodItem?.id) {
                            setBodItem({
                                id: bodItem?.id,
                                bodText: fieldStateText ?? '',
                            })
                        }
                        setEditError?.(false)
                        saveUpdatedContentItemValues(updatedTemplate)
                    }
                }
            }
            handleMouseLeave()
        },
        [
            fieldState,
            hasChanges,
            saveUpdatedContentItemValues,
            editable,
            template,
            updateElementOptionState,
            handleUpdateTemplate,
            handleMouseLeave,
            element,
            bodItem,
            saveNewlyAddedProperty,
            handleChange,
            showGlobalSnackbar,
            handleEditKeyChanges,
            editorRef,
            getSiblings,
        ]
    )

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLSpanElement>) => {
            if (event.key === 'Escape' && fieldState.type === 'NewlyAdded') {
                cancelAddProperty(false)
                setIsFocusManaged(true)
            } else if (
                // This will handle the case when there is already a text inside the pill but the user pressed Escape
                event.key === 'Escape' &&
                (fieldState.type === 'newPill' ||
                    fieldState.type === 'newUOM' ||
                    fieldState.type === 'newReplacement')
            ) {
                event.preventDefault()
                event.stopPropagation()
                displayPillDeleteDialog?.(true)
            }
        },
        [cancelAddProperty, fieldState]
    )

    const getIsElementDeleted = React.useCallback(
        () =>
            element?.type === 'SelectionOption' &&
            !!element?.elements &&
            element?.elements?.length > 0
                ? (element?.elements[0]?.changeType as TrackChangeType) &&
                  (element?.elements[0]
                      ?.changeType as TrackChangeType).toLowerCase() ===
                      TrackChangeType.DELETED.toLowerCase()
                : (element?.changeType as TrackChangeType) &&
                  (element?.changeType as TrackChangeType).toLowerCase() ===
                      TrackChangeType.DELETED.toLowerCase(),
        [element]
    )

    const generateEditorComponent = React.useCallback(() => {
        // if the strike through doesn't appear  after delete
        //Please check this function i.e getIsElementDeleted()
        const isDeleted = getIsElementDeleted()
        const isElementHidden =
            (!isTrackChangeToggleOn && isDeleted) ||
            (!areHiddenPropertiesVisible &&
                fieldState.optionItemState === 'InActive')
        const isElementDisabled =
            !editable || (isTrackChangeToggleOn && isDeleted)
        if (isElementHidden) {
            return
        }
        switch (fieldState.type as TextVariant) {
            case TextVariants.uomInPill:
                if (isElementHidden) {
                    return
                }
                return (
                    <>
                        <PropertyEditorEditableText
                            element={element}
                            editable={editable}
                            editorRef={setEditorRef}
                            data-testid={`inputElementText${element.id}`}
                            identifier={`replacementOption${element.id}`}
                            key={`editableText${fieldState.id}`}
                            additionalBlockedCharacters={
                                forbiddenParagraphCharacters
                            }
                            value={fieldState?.text}
                            handleChangepillInUom={handleChangepillInUom}
                            onChange={handleChange}
                            variant={TextVariants.uomInPill}
                            bodId={
                                fieldState.id === bodItem.id
                                    ? bodItem.id
                                    : undefined
                            }
                            insertUomPill={insertUomPill}
                            onDeleteOption={handleDeleteOption}
                            onBlurInput={onBlurInput}
                            isUserAdded={template?.isUserAdded ?? false}
                            changeType={element?.changeType as TrackChangeType}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            Uomcreate={true}
                            template={template}
                            setPromoteDemoteRequirements={
                                setPromoteDemoteRequirements
                            }
                            triggerAddNewProperty={triggerAddNewProperty}
                            fieldState={fieldState}
                            setFieldState={setFieldState}
                            parentTemplate={parentTemplate}
                        />
                    </>
                )
            case TextVariants.ReplacementOption:
            case TextVariants.newReplacement:
                // we use outOfBoundsContentType + 'InActive' option state to determine
                // deleted 'ReplacementOptions' field type and render using proper component.
                const outOfBoundsChangeType = [
                    'disabled',
                    'optiongroup',
                    'deleted',
                ].includes(element?.changeType?.toLowerCase() ?? '')
                if (
                    (fieldState.optionItemState === 'OptedIn' ||
                        fieldState.optionItemState === 'InActive') &&
                    bodItem?.id !== fieldState?.id &&
                    !template.isBOD &&
                    !(
                        fieldState.optionItemState === 'InActive' &&
                        outOfBoundsChangeType &&
                        contentTeamFromStudioPage
                    )
                ) {
                    return (
                        <PropertyEditorOptionItem
                            value={fieldState.text}
                            state={fieldState.optionItemState}
                            data-testid={`selectionItem${fieldState.id}`}
                            onClick={handleSelectionOptionChange}
                            elementId={fieldState.id}
                            onRightClick={handleRightClick}
                            isBestPractice={fieldState.isBestPractice}
                            isGlobalBestPractice={
                                fieldState.isGlobalBestPractice
                            }
                            isPremiumPlus={fieldState.isPremiumPlus}
                            disabled={isElementDisabled}
                            onDeleteOption={handleDeleteOption}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            isManufacturerContentPills={
                                template?.isBOD || template?.isManufacturers
                            }
                            isDeleteIconVisible={false}
                            changeType={element?.changeType as TrackChangeType}
                            fieldStateType={fieldState.type}
                            isBestPracticeViewDisabled={
                                isBestPracticeViewDisabled
                            }
                            isBestPracticeEditDisabled={
                                isBestPracticeEditDisabled
                            }
                        />
                    )
                } else {
                    return (
                        <PropertyEditorEditableText
                            element={element}
                            editable={editable}
                            editorRef={setEditorRef}
                            data-testid={
                                fieldState?.type === 'newReplacement'
                                    ? 'newReplacement'
                                    : `replacementOption${element.id}`
                            }
                            identifier={`replacementOptionText${element.id}`}
                            key={`editableText${fieldState.id}`}
                            value={sanitizeHtml(fieldState.text ?? '', {
                                allowedTags: [],
                            })}
                            onChange={handleChange}
                            variant={
                                fieldState?.type === 'newReplacement'
                                    ? 'newReplacement'
                                    : 'boxed'
                            }
                            fieldStateType={fieldState.type}
                            bodId={
                                fieldState.id === bodItem.id
                                    ? bodItem.id
                                    : undefined
                            }
                            isUserAdded={template?.isUserAdded ?? false}
                            changeType={element?.changeType as TrackChangeType}
                            template={template}
                            setPromoteDemoteRequirements={
                                setPromoteDemoteRequirements
                            }
                            onBlurInput={onBlurInput}
                            onDeleteOption={handleDeleteOption}
                            insertUomPill={insertUomPill}
                            setFieldState={setFieldState}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            triggerAddNewProperty={triggerAddNewProperty}
                            displayPillDeleteDialog={displayPillDeleteDialog}
                            parentTemplate={parentTemplate}
                        />
                    )
                }

            case TextVariants.UnitsOfMeasure:
            case TextVariants.newUOM:
                return (
                    <>
                        {UnitsOfMeasure.BOTH.toLowerCase().includes(
                            unitsOfMeasure?.toLowerCase()
                        ) &&
                            showMetric && <span>(</span>}
                        <PropertyEditorEditableText
                            element={element}
                            editable={editable}
                            editorRef={setEditorRef}
                            data-testid={`inputElementText${element.id}`}
                            identifier={
                                fieldState.type === 'UnitsOfMeasure'
                                    ? `inputElementText${element.id}${
                                          showMetric ? '-metric' : '-imperial'
                                      }`
                                    : `replacementOption${element.id}`
                            }
                            key={`editableText${fieldState.id}`}
                            additionalBlockedCharacters={
                                forbiddenParagraphCharacters
                            }
                            value={sanitizeHtml(
                                showMetric
                                    ? fieldState.metricUnits ?? ''
                                    : fieldState.imperialUnits ?? '',
                                {
                                    allowedTags: [],
                                }
                            )}
                            onChange={handleChange}
                            variant={
                                fieldState?.type === 'newUOM'
                                    ? 'newUOM'
                                    : 'boxed'
                            }
                            focusState={showImperial && !showMetric}
                            bodId={
                                fieldState.id === bodItem.id
                                    ? bodItem.id
                                    : undefined
                            }
                            onDeleteOption={handleDeleteOption}
                            isUserAdded={template?.isUserAdded ?? false}
                            changeType={element?.changeType as TrackChangeType}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            template={template}
                            setPromoteDemoteRequirements={
                                setPromoteDemoteRequirements
                            }
                            triggerAddNewProperty={triggerAddNewProperty}
                            displayPillDeleteDialog={displayPillDeleteDialog}
                            parentTemplate={parentTemplate}
                            setFieldState={setFieldState}
                        />
                        {UnitsOfMeasure.BOTH.toLowerCase().includes(
                            unitsOfMeasure?.toLowerCase()
                        ) &&
                            showMetric && <span>)</span>}
                    </>
                )
            case TextVariants.Text:
            case TextVariants.NewlyAdded:
            case TextVariants.Hyperlink:
                let paragraphContent = fieldState.text
                let propertyKey = ''
                const firstColonIndex = fieldState?.text?.indexOf(':') ?? -1
                if (elementIndex === 0) {
                    propertyKey =
                        fieldState?.text?.substring(
                            0,
                            firstColonIndex >= 0 ? firstColonIndex + 1 : 0
                        ) ?? ''
                    paragraphContent = fieldState.text?.substring(
                        firstColonIndex >= 0 ? firstColonIndex + 1 : 0
                    )
                }
                const isUserAdded = template?.isUserAdded
                const editableTextVariant =
                    fieldState.type === 'NewlyAdded' ? 'newlyadded' : 'plain'
                if (isEditError) {
                    return (
                        <GridContainer direction="column">
                            className={classes.borderBottom}
                            <GridItem
                                data-testid="templateItemEdit"
                                className={clsx(
                                    classes.contentItemContainer,
                                    classes.draggedItemErrorDarkBorder,
                                    classes.draggedItemErrorLightBackgroundColor
                                )}
                            >
                                <div
                                    className={clsx(
                                        classes.contentItemDragHandleContainer,
                                        classes.draggedItemErrorDarkBackgroundColor
                                    )}
                                >
                                    <CircularProgress
                                        size={15}
                                        className={classes.newPropertyLoading}
                                    />
                                </div>
                                {prefix}&nbsp;&nbsp;&nbsp;&nbsp;
                                <PropertyEditorEditableText
                                    element={element}
                                    key={`${element.id}`}
                                    identifier={`spanElementText${element.id}`}
                                    editable={editable}
                                    data-testid={`spanElementText${fieldState.id}`}
                                    editorRef={setEditorRef}
                                    value={fieldState.text}
                                    titleText={
                                        isPropertyKeyEditable(
                                            selectedProjectNode!,
                                            template
                                        )
                                            ? undefined
                                            : propertyKey
                                    }
                                    additionalBlockedCharacters={
                                        !contentTeamFromStudioPage &&
                                        ((firstColonIndex > -1 &&
                                            !template?.isUserAdded) ||
                                            !template?.isUserAdded)
                                            ? forbiddenParagraphCharactersForKeyedParagraph
                                            : forbiddenParagraphCharacters
                                    }
                                    variant={editableTextVariant}
                                    hasErrors={hasError}
                                    setHasErrors={setHasError}
                                    isUserAdded={isUserAdded ?? false}
                                    changeType={
                                        element?.changeType as TrackChangeType
                                    }
                                    insertUomPill={insertUomPill}
                                    setFieldState={setFieldState}
                                    contentTeamFromStudioPage={
                                        contentTeamFromStudioPage
                                    }
                                    saveChanges={handleSavingTextChanges}
                                    isDeleteFormatted={isDeleteFormatted}
                                    template={template}
                                    setPromoteDemoteRequirements={
                                        setPromoteDemoteRequirements
                                    }
                                    triggerAddNewProperty={
                                        triggerAddNewProperty
                                    }
                                    parentTemplate={parentTemplate}
                                />
                            </GridItem>
                        </GridContainer>
                    )
                }

                return (
                    <PropertyEditorEditableText
                        element={element}
                        key={`${element.id}`}
                        identifier={`spanElementText${element.id}`}
                        editable={editable}
                        data-testid={`spanElementText${fieldState.id}`}
                        editorRef={setEditorRef}
                        value={
                            isPropertyKeyEditable(
                                selectedProjectNode!,
                                template,
                                contentTeamFromStudioPage
                            )
                                ? fieldState.text
                                : paragraphContent
                        }
                        titleText={
                            isPropertyKeyEditable(
                                selectedProjectNode!,
                                template,
                                contentTeamFromStudioPage
                            )
                                ? undefined
                                : propertyKey
                        }
                        fieldState={fieldState}
                        additionalBlockedCharacters={
                            !contentTeamFromStudioPage &&
                            ((firstColonIndex > -1 && !template?.isUserAdded) ||
                                !template?.isUserAdded)
                                ? forbiddenParagraphCharactersForKeyedParagraph
                                : forbiddenParagraphCharacters
                        }
                        isToUpdateElement={
                            (hasPreviousChanges && !hasChanges) ||
                            !!element.changeType
                        }
                        saveChanges={handleSavingTextChanges}
                        variant={editableTextVariant}
                        hasErrors={hasError}
                        setHasErrors={setHasError}
                        isUserAdded={isUserAdded ?? false}
                        changeType={element?.changeType as TrackChangeType}
                        isDeleteFormatted={isDeleteFormatted}
                        template={template}
                        setPromoteDemoteRequirements={
                            setPromoteDemoteRequirements
                        }
                        contentTeamFromStudioPage={contentTeamFromStudioPage}
                        triggerAddNewProperty={triggerAddNewProperty}
                        onHtmlParagraphPaste={handleHtmlParagraphPaste}
                        parentTemplate={parentTemplate}
                        setProperty={setProperty}
                        setFieldState={setFieldState}
                        elementId={fieldState.id}
                        onRightClick={handleRightClick}
                        isBestPractice={fieldState.isBestPractice}
                        isGlobalBestPractice={fieldState.isGlobalBestPractice}
                        isBestPracticeEditDisabled={isBestPracticeEditDisabled}
                    />
                )
            case TextVariants.Hyperlink:
                // split property key from the rest of the content
                let hyperlinkContent = fieldState.text
                let hyperlinkPropertyKey = ''
                const firstHyperlinkColonIndex =
                    fieldState?.text?.indexOf(':') ?? -1
                if (elementIndex === 0) {
                    hyperlinkPropertyKey =
                        fieldState?.text?.substring(
                            0,
                            firstHyperlinkColonIndex >= 0
                                ? firstHyperlinkColonIndex + 1
                                : 0
                        ) ?? ''
                    hyperlinkContent = fieldState.text?.substring(
                        firstHyperlinkColonIndex >= 0
                            ? firstHyperlinkColonIndex + 1
                            : 0
                    )
                }

                return (
                    <PropertyEditorEditableText
                        element={element}
                        editable={editable}
                        value={hyperlinkContent}
                        titleText={hyperlinkPropertyKey}
                        data-testid={`hyperlink${element.id}`}
                        identifier={`hyperlink${element.id}`}
                        onChange={handleChange}
                        editorRef={setEditorRef}
                        additionalBlockedCharacters={
                            (firstHyperlinkColonIndex > -1 &&
                                !template?.isUserAdded) ||
                            !template?.isUserAdded
                                ? forbiddenParagraphCharactersForKeyedParagraph
                                : forbiddenParagraphCharacters
                        }
                        variant="plain"
                        isUserAdded={template?.isUserAdded ?? false}
                        changeType={element?.changeType as TrackChangeType}
                        contentTeamFromStudioPage={contentTeamFromStudioPage}
                        template={template}
                        setPromoteDemoteRequirements={
                            setPromoteDemoteRequirements
                        }
                        triggerAddNewProperty={triggerAddNewProperty}
                        parentTemplate={parentTemplate}
                        setFieldState={setFieldState}
                        saveChanges={handleSavingTextChanges}
                    />
                )
            case TextVariants.SelectionOption:
            case TextVariants.newPill:
                // If SelectionOption don't have a value it will not save the pill

                if (
                    fieldState.type === 'SelectionOption' &&
                    !fieldState.text &&
                    (isContentTeamUser || isOMUser)
                ) {
                    return
                }
                if (fieldState.type === TextVariants.newPill) {
                    return (
                        <PropertyEditorEditableText
                            fieldState={fieldState}
                            element={element}
                            editable={editable}
                            editorRef={setEditorRef}
                            onChange={handleChangepillInUom}
                            data-testid="newPillTest"
                            identifier={`replacementOptionText${element.id}`}
                            key={`editableText${fieldState.id}`}
                            value={sanitizeHtml(fieldState.text ?? '', {
                                allowedTags: [],
                            })}
                            variant={TextVariants.newPill}
                            fieldStateType={fieldState.type}
                            bodId={
                                fieldState.id === bodItem.id
                                    ? bodItem.id
                                    : undefined
                            }
                            isUserAdded={template?.isUserAdded ?? false}
                            changeType={element?.changeType as TrackChangeType}
                            onBlurInput={onBlurInput}
                            onDeleteOption={handleDeleteOption}
                            insertUomPill={insertUomPill}
                            setFieldState={setFieldState}
                            displayPillDeleteDialog={displayPillDeleteDialog}
                            parentTemplate={parentTemplate}
                        />
                    )
                }

                return (
                    <PropertyEditorOptionItem
                        value={fieldState.text}
                        state={fieldState.optionItemState}
                        data-testid={`selectionItem${fieldState.id}`}
                        onClick={handleSelectionOptionChange}
                        elementId={fieldState.id}
                        onRightClick={handleRightClick}
                        isBestPractice={fieldState.isBestPractice}
                        isGlobalBestPractice={fieldState.isGlobalBestPractice}
                        isPremiumPlus={fieldState.isPremiumPlus}
                        disabled={isElementDisabled}
                        onDeleteOption={handleDeleteOption}
                        contentTeamFromStudioPage={contentTeamFromStudioPage}
                        isManufacturerContentPills={
                            template?.isBOD || template?.isManufacturers
                        }
                        isDeleteIconVisible={editable} // Delete icon is hidden if the element is not editable
                        changeType={
                            (element?.changeType !== null
                                ? element?.changeType
                                : element?.elements?.[0]
                                      ?.changeType) as TrackChangeType
                        }
                        isBPMenuOpen={isBPMenuOpen}
                        setIsBPMenuOpen={setIsBPMenuOpen}
                        mouseOverElement={mouseOverElement}
                        setMouseOverElement={setMouseOverElement}
                        isBestPracticeViewDisabled={isBestPracticeViewDisabled}
                        isBestPracticeEditDisabled={isBestPracticeEditDisabled}
                    />
                )
        }
    }, [
        fieldState,
        handleSelectionOptionChange,
        triggerAddNewProperty,
        handleRightClick,
        editable,
        setEditorRef,
        element,
        handleChange,
        insertUomPill,
        unitsOfMeasure,
        showMetric,
        handleChangepillInUom,
        bodItem,
        hasError,
        elementIndex,
        template,
        handleBlur,
        handleUpdateTemplate,
        isEditError,
        isParentProductGroup,
        isDeleteFormatted,
        hasChanges,
        hasPreviousChanges,
        checkPillWithText,
        isBPMenuOpen,
        mouseOverElement,
        isBestPracticeViewDisabled,
        isBestPracticeEditDisabled,
    ])

    const handleBPfieldChange = React.useCallback(
        (isBP: boolean, isGBP: boolean) => {
            const updatedValue = {
                id: fieldState.id,
                isBestPractice: isBP,
                isGlobalBestPractice: isGBP,
                text: fieldState.text,
            }
            const updatedTemplate = getUpdatedTemplate(template, updatedValue)
            const parentId = getParentContentId(updatedTemplate, fieldState)
            const updatedOptionTemplate = parentId
                ? updateElementOptionState(parentId, updatedTemplate)
                : updatedTemplate

            handleUpdateTemplate?.(updatedOptionTemplate)
            setFieldState({
                ...fieldState,
                ...updatedValue,
            })
        },
        [
            fieldState,
            getUpdatedTemplate,
            updateElementOptionState,
            handleUpdateTemplate,
            setFieldState,
        ]
    )
    const handleBestPracticesMutationData = React.useCallback(
        (mutationData: Partial<BestPracticeData> | null) => {
            setPopoverAnchor(null)
            if (mutationData) {
                const isMutationUpdated = compareBestPracticesMutation(
                    bestPracticeElementData,
                    projectData?.getProject,
                    mutationData
                )
                if (isMutationUpdated) {
                    const defaultInput = {
                        ...mutationData,
                        parentProjectId: projectData?.getProject.id,
                        contentItemId: template?.contentItem?.id,
                        contentItemElementId:
                            selectedPillElementId ?? undefined, // this value will be undefined if we set BP tags to elements other than pills
                    }
                    defaultInput.projectElementNodeId = getPersistedState?.(
                        'selectedNodeId'
                    )

                    const mutationInput =
                        bestPracticeElementData?.getBestPractice?.id &&
                        bestPracticeElementData?.getBestPractice
                            ?.parentProjectId === projectData?.getProject.id
                            ? {
                                  ...defaultInput,
                                  id:
                                      bestPracticeElementData?.getBestPractice
                                          ?.id,
                              }
                            : defaultInput

                    // Use the callback from DraggableItem if provided (only when feature flag is enabled)
                    // Skip callback for pills (when contentItemElementId is present) - pills handle BP at element level, not article level
                    if (
                        processEnv.REACT_APP_EnableSmartProjectCopy === '1' &&
                        onBestPracticesMutationData &&
                        !mutationInput.contentItemElementId
                    ) {
                        onBestPracticesMutationData(mutationData, mutationInput)
                        return
                    }

                    setAddUpdateBestPractice({
                        variables: {
                            input: mutationInput,
                        },
                    })
                }
            } else {
                // removed the other condition to allow user to change the BP setting on different projects
                if (bestPracticeElementData?.getBestPractice?.id) {
                    setRemoveBestPractice({
                        variables: {
                            input: {
                                id:
                                    bestPracticeElementData?.getBestPractice
                                        ?.id,
                            },
                        },
                    })
                }
            }
            setIsBPMenuOpen(false)
            setMouseOverElement(null)
        },
        [
            onBestPracticesMutationData,
            bestPracticeElementData,
            projectData,
            selectedPillElementId,
            setSelectedPillElementId,
            template,
            setAddUpdateBestPractice,
            setMouseOverElement,
            getPersistedState,
        ]
    )
    React.useEffect(() => {
        if (
            displayText !== fieldState?.text &&
            fieldState?.type === 'Text' &&
            !hasChanges &&
            elementRef.current
        ) {
            setFieldState({
                ...element,
                text:
                    getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure
                    ) ?? '',
            })
        }

        if (
            fieldState?.type === 'UnitsOfMeasure' &&
            !hasChanges &&
            elementRef.current &&
            ((showMetric && fieldState.text !== element.metricUnits) ||
                (showImperial && fieldState.text !== element.imperialUnits) ||
                (UnitsOfMeasure.BOTH.toLowerCase().includes(
                    unitsOfMeasure?.toLowerCase()
                ) &&
                    (fieldState.metricUnits !== element.metricUnits ||
                        fieldState.imperialUnits !== element.imperialUnits)))
        ) {
            setFieldState({
                ...element,
                text:
                    getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure
                    ) ?? '',
            })
            if (element.changeType === null) {
                setTimeout(() => {
                    resetOnFocusProperty()
                }, 1000)
            }
        }
    }, [displayText, hasChanges, showMetric, showImperial, element])

    React.useEffect(() => {
        if (!contentTeamFromStudioPage) return
        // this block is to make product type name non editable for every level of Product Type
        const isManufacturerTemplateParent =
            template?.children?.filter((item) => item?.isManufacturers)
                ?.length !== 0
        if (isManufacturerTemplateParent) {
            // if the template is parent of manufacturer paragraph , find out the element containing first colon to append colon with Product Type name only

            handleUpdateTemplate?.({
                ...template,
                contentItem: {
                    ...template?.contentItem,
                    elements: getUpdatedPTElementsWithAppendedColon(template),
                },
            })
        }
    }, [contentTeamFromStudioPage])
    React.useEffect(() => {
        if (
            fieldState?.type === 'newUOM' &&
            !hasChanges &&
            elementRef.current &&
            ((showMetric && fieldState.text !== element.metricUnits) ||
                (showImperial && fieldState.text !== element.imperialUnits) ||
                (UnitsOfMeasure.BOTH.toLowerCase().includes(
                    unitsOfMeasure?.toLowerCase()
                ) &&
                    (fieldState.metricUnits !== element.metricUnits ||
                        fieldState.imperialUnits !== element.imperialUnits)))
        ) {
            setFieldState({
                ...element,
                text:
                    getDisplayText(
                        element,
                        showMetric,
                        showImperial,
                        unitsOfMeasure
                    ) ?? ' ',
            })
        }
    }, [
        fieldState?.type,
        fieldState?.text,
        fieldState?.metricUnits,
        fieldState?.imperialUnits,
        showImperial,
        showMetric,
    ])
    const handleHtmlParagraphPaste = useCallback(
        (clipboardHtml: string) => {
            // Discard any edits to the active item that user pasted into.
            // (Paste will be handled by the backend.)
            setHasError(false)
            setHasChanges(false)

            onHtmlParagraphPaste?.(clipboardHtml)
        },
        [onHtmlParagraphPaste]
    )
    React.useEffect(() => {
        if (
            (element.type === 'SelectionOption' ||
                element.type === 'ReplacementOption') &&
            elementRef.current
        ) {
            setFieldState((past) => {
                if (element.type === 'SelectionOption') {
                    const updatedFieldstate =
                        past.optionItemState !== element.optionItemState
                            ? element
                            : past
                    return {
                        ...updatedFieldstate,
                        text:
                            getDisplayText(
                                element,
                                showMetric,
                                showImperial,
                                unitsOfMeasure
                            ) ?? '',
                    }
                } else {
                    if (past.text !== element.text) {
                        return {
                            ...element,
                            text:
                                getDisplayText(
                                    element,
                                    showMetric,
                                    showImperial,
                                    unitsOfMeasure
                                ) ?? element.text,
                            optionItemState: element.optionItemState,
                        }
                    } else {
                        return {
                            ...past,
                            text:
                                getDisplayText(
                                    element,
                                    showMetric,
                                    showImperial,
                                    unitsOfMeasure
                                ) ?? '',
                            optionItemState: element.optionItemState,
                        }
                    }
                }
            })
        }
    }, [unitsOfMeasure, element, showImperial, showMetric])

    React.useEffect(() => {
        if (
            (fieldState?.type !== 'SelectionOption' &&
                fieldState?.type !== 'OptionGroup' &&
                fieldState?.type !== 'ReplacementOption') ||
            selectedItemIdForPills !== selectedParagraphItemId
        )
            return
        if (selectedParagraphActionState === 'Reset') {
            handleSelectionOptionChange(false, 'OptedOut')
        } else if (selectedParagraphActionState === 'InActive') {
            handleSelectionOptionChange(true, 'InActive')
        }
    }, [
        selectedParagraphActionState,
        selectedParagraphItemId,
        selectedItemIdForPills,
        fieldState,
        handleSelectionOptionChange,
    ])
    React.useEffect(() => {
        if (elementRef.current) {
            setIsFocusManaged((past) => {
                return past ? !isCancelCancelled : past
            })
        }
    }, [isCancelCancelled])

    React.useEffect(() => {
        if (
            ((propertyToFocus && propertyToFocus === element?.id) ||
                (propertyContentItemId &&
                    propertyContentItemId === template?.id)) &&
            elementRef &&
            elementRef?.current
        ) {
            elementRef?.current?.scrollIntoView?.({
                block: 'center',
            })
        }
    }, [
        propertyToFocus,
        element?.id,
        propertyContentItemId,
        template,
        elementRef,
    ])

    React.useEffect(() => {
        return () => {
            if (elementRef) {
                elementRef.current = false
            }

            if (editorRef) {
                setEditorRef(undefined)
            }
        }
    }, [])

    React.useEffect(() => {
        if (template?.itemId === selectedContent && editorRef !== undefined) {
            ;(editorRef as HTMLInputElement).focus()
        }
    }, [selectedContent, editorRef])

    return (
        <>
            <span
                data-testid={`spanElement${element.id}`}
                onBlur={(event) =>
                    (!isFocusManaged &&
                        element.type !== 'Text' &&
                        element.type !== 'NewlyAdded' &&
                        (element.type === 'newReplacement' ||
                            element.type === 'newUOM')) ||
                    element.type === 'ReplacementOption' ||
                    element.type === 'UnitsOfMeasure'
                        ? handleBlur(event)
                        : undefined
                }
                onFocus={
                    fieldState.type !== 'ReplacementOption' &&
                    fieldState.type !== 'SelectionOption' &&
                    fieldState.type !== 'UnitsOfMeasure' &&
                    fieldState.type !== 'newUOM'
                        ? handleOnFocus
                        : undefined
                }
                className={clsx(
                    classes.editorComponentContainer,
                    ((propertyToFocus && propertyToFocus === element?.id) ||
                        optionGroupToFocus?.some(
                            (item) => item === element?.id || ''
                        )) &&
                        classes.highlightElement,
                    element.type === 'NewlyAdded'
                        ? classes.editorComponentContainerFlex
                        : ''
                )}
                onKeyDown={handleKeyDown}
                ref={elementRef}
                id={`ciet-${template?.id}`}
            >
                {generateEditorComponent()}
            </span>
            {popoverAnchor && !isBestPracticeEditDisabled && (
                <BestPracticesSetting
                    onlyPopover={true}
                    popoverAnchor={popoverAnchor}
                    projectDetail={projectData?.getProject}
                    setBestPracticesMutationData={
                        handleBestPracticesMutationData
                    }
                    data={bestPracticeElementData}
                    loading={bestPracticeElementLoading}
                    isBestPractice={selectedPillIsBP}
                    isGlobalBestPractice={selectedPillIsGBP}
                    isDisabled={isBestPracticeEditDisabled}
                />
            )}
            <ConfirmationDialog
                openDialog={showPillDeleteDialog}
                handleDialogClose={() => {
                    displayPillDeleteDialog(false)
                }}
                dialogActionCallback={() => {
                    displayPillDeleteDialog(false)
                    handleDeleteOption()
                }}
                contentNode={
                    'This action will remove this property from your project.'
                }
                size="small"
                dialogHeading={'Cancel Add'}
            />
        </>
    )
}

export default ContentItemElementText
